import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  api, ErreurApi,
  type CatalogueDemandes, type PreRemplissageDemande, type FicheDemande, type FileDemandes, type TypeDemande,
} from './api';
import { Icone } from './icones';
import { FormulaireLisible } from './formulaire-lisible';
import { LIBELLE_PIECE, PIECES_IDENTITE } from '../../src/partage/pieces-identite.js';
import {
  ChampNationalite, ChampNin, ChampPortrait, ChampsAdresse, LIENS_TEMOIN, LIEN_AUTRE, SaisieMajuscule,
  type Adresse,
} from './champs-guichet';
/**
 * ⚠️ LES MENUS DE LA SUCCESSION LISENT LE VOCABULAIRE PARTAGÉ. C'est le défaut
 * exact que `tests/pieces-identite.test.ts` a attrapé sur les pièces d'identité :
 * cet écran en portait une QUATRIÈME copie, codée en dur, dont la valeur par
 * défaut était refusée par la base. Un menu qui propose une valeur que la base
 * rejette rend 400 sur un champ que l'agent vient de choisir dans une liste.
 */
import {
  LIBELLE_QUALITE, LIBELLE_SOURCE_DECES, LIBELLE_SOURCE_DEVOLUTION,
  QUALITES_SUCCESSORALES, SOURCES_DECES, SOURCES_DEVOLUTION,
} from '../../src/partage/vocabulaire-succession.js';
/**
 * ⚠️ MÊME RÈGLE POUR LA MAINLEVÉE : les trois sûretés et les six causes
 * d'extinction sont celles du SCHÉMA, pas un menu recopié. Un menu plus large
 * que la garde `fon.garde_mainlevee` rendrait une erreur SQL brute à l'agent.
 */
import {
  CAUSES_EXTINCTION, LIBELLE_CAUSE_EXTINCTION, LIBELLE_SURETE, NATURES_SURETE,
} from '../../src/partage/vocabulaire-surete.js';

/**
 * LE GUICHET — déposer une demande, la suivre, la faire avancer.
 *
 * ⚠️ CET ÉCRAN EST LE PREMIER DU REGISTRE QUI S'ADRESSE À UN AGENT DERRIÈRE UN
 * COMPTOIR. Les autres servent à instruire ; celui-ci sert à RECEVOIR. La
 * différence n'est pas cosmétique : un agent d'accueil a un usager devant lui,
 * il saisit ce que l'usager dit, et il doit pouvoir lui répondre « votre
 * dossier porte ce numéro, il est au service X ». Sans cela, l'usager repart
 * sans rien et revient demander où il en est.
 *
 * ⚠️ LE FORMULAIRE EST EN CADRES, comme une pièce administrative, et pas en
 * une colonne de champs. Le modèle éprouvé est le 6815-EM-SD français —
 * demandeur, situation du bien, personne inscrite, règles d'utilisation — que
 * l'agent lit dans le même ordre que l'usager les énonce.
 */

const LIBELLE_SERVICE: Record<string, string> = {
  administration: 'Direction du Cadastre',
  commune: 'Commune',
  conservation_fonciere: 'Conservation Foncière',
  etude_notariale: 'Étude notariale',
  cabinet_geometre: 'Cabinet de géomètre',
  service_fiscal: 'Service fiscal',
  collectivite: 'Collectivité',
  autre: 'Autre service',
};
const service = (t: string | null | undefined) => (t ? LIBELLE_SERVICE[t] ?? t : '—');

type Onglet = 'file' | 'deposer';

/**
 * ⚠️ `ouvrirType` — L'INTENTION VENUE DU COCKPIT, ET POURQUOI ELLE EXISTE.
 *
 * Les dix entrées de « Ouvrir une opération » du bureau NOMMAIENT chacune une
 * opération et menaient toutes ici, sans rien transmettre : « Donation entre
 * vifs » et « Duplicata de titre » faisaient exactement la même chose — ouvrir
 * la file, et laisser l'agent re-choisir son type dans le catalogue.
 *
 * Ce n'était pas un bouton mort — il naviguait, donc aucun essai ne pouvait le
 * voir. C'est la forme discrète de l'illusion de fonctionnement : un libellé
 * précis pour un geste générique.
 */
export function Guichet(
  { onOuvrirDossier, ouvrirType, parcelleDesignee, onParcelleConsommee }:
  {
    onOuvrirDossier: (reference: string) => void;
    ouvrirType?: string | null;
    /** La parcelle DÉSIGNÉE SUR LA CARTE — §2.3, jamais tapée à la main. */
    parcelleDesignee?: string | null;
    onParcelleConsommee?: () => void;
  },
) {
  /* ⚠️ UNE PARCELLE DÉSIGNÉE SUR LA CARTE OUVRE LE DÉPÔT, pas la file : on
     vient d'un fonds précis, pas d'une liste de dossiers à suivre. */
  const [onglet, setOnglet] = useState<Onglet>(ouvrirType || parcelleDesignee ? 'deposer' : 'file');
  useEffect(() => { if (parcelleDesignee) setOnglet('deposer'); }, [parcelleDesignee]);
  const [file, setFile] = useState<FileDemandes | null>(null);
  const [catalogue, setCatalogue] = useState<CatalogueDemandes | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [ouverte, setOuverte] = useState<string | null>(null);

  const charger = useCallback(async () => {
    setErreur(null);
    try {
      const [f, c] = await Promise.all([api.demandes.file(), api.demandes.catalogue()]);
      setFile(f); setCatalogue(c);
    } catch (e) {
      setErreur(e instanceof ErreurApi && e.estRefus
        ? "Votre rôle ne permet pas d'accéder au guichet (fon.demande:lire)."
        : String((e as Error).message ?? e));
    }
  }, []);
  useEffect(() => { void charger(); }, [charger]);
  // ⚠️ L'intention peut arriver APRÈS le montage : le cockpit reste monté et
  // change seulement sa valeur. Sans cet effet, le premier clic ouvrirait le
  // bon type et les suivants ne feraient plus rien.
  useEffect(() => { if (ouvrirType) setOnglet('deposer'); }, [ouvrirType]);

  if (ouverte) {
    return <FicheDemandeVue reference={ouverte}
      onFerme={() => { setOuverte(null); void charger(); }}
      onOuvrirDossier={onOuvrirDossier} />;
  }

  return (
    <div className="tableau-bord">
      <header className="entete-scene">
        <h2>Guichet</h2>
        <div className="actions">
          {catalogue?.monOrganisation && (
            <span className="jeton">{catalogue.monOrganisation}</span>
          )}
          <div className="onglets">
            <button className={onglet === 'file' ? 'actif' : ''} onClick={() => setOnglet('file')}>
              Ma file{file?.total ? ` · ${file.total}` : ''}
            </button>
            <button className={onglet === 'deposer' ? 'actif' : ''} onClick={() => setOnglet('deposer')}>
              Déposer une demande
            </button>
          </div>
        </div>
      </header>

      {erreur && <p className="erreur" role="alert">{erreur}</p>}

      {/* ⚠️ « Aucune file » et « aucune demande » se ressemblent à l'écran, et
          n'appellent pas le même geste. Le service les sépare, on les affiche
          séparément. */}
      {file && file.monService === null && (
        <div className="avertissement danger">
          <strong>Aucune file ne peut vous être présentée.</strong> {file.motif}
        </div>
      )}

      {onglet === 'file' ? (
        <FileService file={file} onOuvrir={setOuverte} onOuvrirDossier={onOuvrirDossier} />
      ) : (
        <ChoixDemande catalogue={catalogue} typeInitial={ouvrirType ?? null}
                      parcelleDesignee={parcelleDesignee}
                      onParcelleConsommee={onParcelleConsommee}
                      onDepose={(ref) => { setOuverte(ref); void charger(); }} />
      )}
    </div>
  );
}

function FileService(
  { file, onOuvrir, onOuvrirDossier }:
  { file: FileDemandes | null; onOuvrir: (r: string) => void; onOuvrirDossier: (r: string) => void },
) {
  if (!file) return <div className="attente-plein">Chargement de la file…</div>;
  if (file.monService === null) return null;

  if (file.demandes.length === 0) {
    return (
      <p className="vide">
        Aucune demande n'attend {service(file.monService)}. Les demandes arrivent ici
        automatiquement dès que le service précédent a terminé.
      </p>
    );
  }

  return (
    <>
      {Boolean(file.enRetard) && (
        <div className="avertissement danger">
          <Icone nom="alerte" taille={16} />{' '}
          <strong>{file.enRetard} demande(s) ont dépassé leur délai réglementaire.</strong>{' '}
          Elles sont en tête de liste.
        </div>
      )}
      <table className="tableau large">
        <thead>
          <tr><th>Demande</th><th>Nature</th><th>Requérant</th><th>Parcelle</th>
              <th>Dépôt</th><th>Étape</th><th>Dossier</th></tr>
        </thead>
        <tbody>
          {file.demandes.map((d) => (
            <tr key={d.reference} className={d.delai_depasse ? 'ligne-alerte' : undefined}>
              <td>
                <button className="lien" onClick={() => onOuvrir(d.reference)}>
                  <code>{d.reference}</code>
                </button>
              </td>
              <td>{d.libelle}</td>
              <td>{d.requerant}</td>
              <td>{d.parcelle ? <code>{d.parcelle}</code> : <span className="discret">—</span>}</td>
              <td className="nombre">
                {d.jours_depuis_depot} j
                {d.delai_depasse && d.delai_jours
                  ? <span className="etat rouge" style={{ marginLeft: 6 }}>délai {d.delai_jours} j dépassé</span>
                  : null}
              </td>
              <td className="nombre">{d.position_circuit + 1}/{d.circuit.length}</td>
              <td>
                <button className="lien" onClick={() => onOuvrirDossier(d.affaire)}>
                  <code>{d.affaire}</code>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function ChoixDemande(
  { catalogue, onDepose, typeInitial, parcelleDesignee, onParcelleConsommee }:
  {
    catalogue: CatalogueDemandes | null; onDepose: (r: string) => void; typeInitial?: string | null;
    parcelleDesignee?: string | null; onParcelleConsommee?: () => void;
  },
) {
  const [choisi, setChoisi] = useState<TypeDemande | null>(null);
  /**
   * ⚠️ UN TYPE FERMÉ N'OUVRE PAS UN FORMULAIRE — il laisse voir sa carte et son
   * motif. Ouvrir le formulaire d'un type non déposable donnerait un écran de
   * saisie qui échoue au dépôt, après que l'agent l'a rempli : le refus doit
   * arriver AVANT la saisie, pas après.
   *
   * ⚠️ ET UN TYPE INCONNU NE FAIT RIEN PLUTÔT QUE N'IMPORTE QUOI. Le catalogue
   * vient de la base ; une intention qui n'y correspond à rien signifie que le
   * type a été retiré depuis l'affichage du cockpit. On laisse alors le
   * catalogue visible, qui est la réponse honnête.
   */
  useEffect(() => {
    if (!typeInitial || !catalogue) return;
    const t = catalogue.types.find((x) => x.code === typeInitial);
    if (t && t.deposable && t.formulaireDisponible) setChoisi(t);
  }, [typeInitial, catalogue]);
  if (!catalogue) return <div className="attente-plein">Chargement du catalogue…</div>;
  if (choisi) {
    return (
      <FormulaireDemande
        type={choisi} onFerme={() => setChoisi(null)} onDepose={onDepose}
        parcelleDesignee={parcelleDesignee} onParcelleConsommee={onParcelleConsommee}
      />
    );
  }

  return (
    <>
      <div className="avertissement">
        <strong>La validation du formulaire ouvre un dossier de suivi</strong> et transmet
        la demande au service compétent. L'usager repart avec un numéro de dossier.
      </div>
      <div className="cartes-demandes">
        {catalogue.types.map((t) => {
          const ouvert = t.deposable && t.formulaireDisponible;
          return (
            <button key={t.code} className={`carte-demande ${ouvert ? '' : 'inactive'}`}
                    disabled={!ouvert}
                    title={ouvert ? t.objet : (t.motifFerme ?? "Formulaire non disponible")}
                    onClick={() => setChoisi(t)}>
              <div className="cd-tete">
                <strong>{t.libelle}</strong>
                {t.delai_jours && <span className="jeton">{t.delai_jours} j</span>}
              </div>
              <p className="cd-objet">{t.objet}</p>
              <div className="cd-circuit">
                {t.circuit.map((s, i) => (
                  <span key={s}>
                    {i > 0 && <Icone nom="chevron" taille={11} />}
                    {service(s)}
                  </span>
                ))}
              </div>
              {/* ⚠️ ON DIT POURQUOI C'EST FERMÉ. Masquer la carte ferait croire
                  que la demande n'existe pas, et l'agent chercherait ailleurs. */}
              {!ouvert && <p className="cd-ferme">{t.motifFerme ?? "Formulaire non encore disponible."}</p>}
            </button>
          );
        })}
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   LE FORMULAIRE — en cadres numérotés, comme une pièce administrative.

   ⚠️ LES CADRES NE SONT PAS UNE DÉCORATION. Un agent au comptoir suit ce que
   l'usager lui dit : d'abord qui il est, puis de quel bien il parle, puis ce
   qu'il veut. Une colonne de trente champs oblige à chercher ; des cadres
   permettent de répondre dans l'ordre, et de reprendre au bon endroit quand
   l'usager se corrige.
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * ⚠️⚠️ QUATRIÈME COPIE DE LA LISTE DES PIÈCES, RETIRÉE LE 14/09/2026. Elle
 * était codée en dur ici, avec ses propres libellés, et sa valeur PAR DÉFAUT
 * était `carte_nationale` — que `identifiant_personne_type_check` REFUSE. Un
 * guichet qui propose d'abord une pièce que le registre n'accepte pas remplit
 * ses formulaires d'une valeur inscriptible nulle part, et personne ne s'en
 * aperçoit tant que rien n'essaie de la verser.
 *
 * Elle dérive maintenant de `src/partage/pieces-identite.ts`, qui est confronté
 * à la contrainte de la base dans les deux sens.
 */
const PIECES: Array<[string, string]> =
  PIECES_IDENTITE.map((v) => [v, LIBELLE_PIECE[v]]);

/**
 * ⚠️ LA PIÈCE COURANTE EN GUINÉE EST LA CARTE D'ÉLECTEUR, pas une carte
 * nationale d'identité — qui n'existe pas dans le vocabulaire du registre. Un
 * défaut mal choisi est celui que la plupart des dossiers porteront : c'est la
 * valeur la plus recopiée du formulaire.
 */
const PIECE_PAR_DEFAUT: string = 'carte_electeur';

/**
 * DU BLOC DÉPOSÉ AU CADRE QUI LE REMPLIT.
 *
 * ⚠️ LA CORRESPONDANCE SE FAIT PAR LE TITRE, PAS PAR UN NUMÉRO. Les numéros de
 * cadre changent d'un type à l'autre — le cadre 3 est « le donateur » pour une
 * donation et « la personne inscrite » pour un extrait. Un tableau de numéros
 * serait faux pour huit types sur dix, et ce fichier a déjà payé une
 * numérotation écrite à la main.
 *
 * ⚠️ LE FRAGMENT EST CHERCHÉ DANS LE TITRE EN MINUSCULES, ACCENTS COMPRIS :
 * « reçoit » doit trouver « Celui qui reçoit », et `toLowerCase()` conserve la
 * cédille. Retirer les accents ici ferait échouer la moitié des blocs.
 */
const BLOC_VERS_TITRE_DE_CADRE: Record<string, string> = {
  demandeur: 'demandeur',
  immeuble: 'immeuble',
  personneInscrite: 'personne inscrite',
  donateur: 'donne', donataire: 'reçoit',
  vendeur: 'vendeur', acquereur: 'acquéreur',
  creancier: 'créancier', debiteur: 'débiteur',
  constituant: 'constituant',
  premierCopermutant: 'premier copermutant', secondCopermutant: 'second copermutant',
  titulaireInscription: "titulaire de l'inscription", proprietaireFonds: 'propriétaire du fonds',
  defunt: 'défunt', heritiers: 'ayants droit', ayantsDroit: 'ayants droit',
  origine: 'tient', origineDetention: 'origine',
  temoins: 'témoins',
  lienEntreParties: 'donné', natureDroitDonne: 'donné',
  objet: 'objet de la demande', motif: 'objet de la demande',
  engagementConfidentialite: 'confidentialité',
};

/**
 * LES DEUX PARTIES D'UN ACTE — leur rôle, leur préfixe et le mot de l'écran.
 *
 * ⚠️ LES RÔLES SONT CEUX DE L'ACTE, PAS DES MOTS D'ÉCRAN. `cedant` et
 * `beneficiaire` viennent de `doc.acte_autorite.composition` (migration 093) ;
 * c'est ce qui permet au demandeur de se déclarer partie une seule fois, pour
 * six types d'acte, sans un champ par type.
 *
 * ⚠️ LE MOT DE L'ÉCRAN DIT LE GESTE, le terme de droit reste entre
 * parenthèses. Demande du fondateur, 24/09/2026 : « donataire et donateur =
 * confusion, il faut changer les termes ». Les CLÉS ne bougent pas — `donateur`
 * et `donataire` sont celles de la base, de la fiche des parties et de l'acte
 * imprimé. Changer le libellé ne coûte rien ; changer la clé forkerait le
 * registre.
 */
const PARTIES_DU_TYPE: Record<string, { cedant: [string, string]; beneficiaire: [string, string] }> = {
  donation: { cedant: ['don', 'celui qui donne (donateur)'], beneficiaire: ['dat', 'celui qui reçoit (donataire)'] },
  vente: { cedant: ['ven', 'le vendeur'], beneficiaire: ['acq', "l'acquéreur"] },
  echange: { cedant: ['cp1', 'le premier copermutant'], beneficiaire: ['cp2', 'le second copermutant'] },
  mainlevee: { cedant: ['cre', 'le créancier qui libère'], beneficiaire: ['deb', 'le débiteur dont le fonds est grevé'] },
  inscription_surete: { cedant: ['cst', 'le constituant'], beneficiaire: ['cre', 'le créancier inscrit'] },
  radiation: { cedant: ['tit', "le titulaire de l'inscription radiée"], beneficiaire: ['prp', 'le propriétaire du fonds apuré'] },
};

/** Ce qui se recopie du cadre 1 vers le cadre de la partie : clé → suffixe. */
/** Le rôle que porte chaque préfixe — pour nommer la personne photographiée. */
const QUI_EST_CE: Record<string, string> = {
  don: 'celui qui donne', dat: 'celui qui reçoit',
  ven: 'le vendeur', acq: "l'acquéreur",
  cre: 'le créancier', deb: 'le débiteur', cst: 'le constituant',
  cp1: 'le premier copermutant', cp2: 'le second copermutant',
  tit: "le titulaire de l'inscription", prp: 'le propriétaire du fonds',
};

const MIROIR_DEMANDEUR: ReadonlyArray<readonly [string, string]> = [
  ['nom', 'Nom'], ['prenoms', 'Prenoms'], ['nin', 'Nin'], ['dateNaissance', 'Naissance'],
  ['lieuNaissance', 'LieuNaissance'], ['nationalite', 'Nationalite'], ['profession', 'Profession'],
  ['pieceType', 'PieceType'], ['pieceNumero', 'PieceNumero'], ['pieceDelivreeLe', 'PieceDelivreeLe'],
  ['telephone', 'Telephone'], ['courriel', 'Courriel'], ['region', 'Region'],
  ['prefecture', 'Prefecture'], ['commune', 'Commune'], ['quartier', 'Quartier'],
];

/**
 * ⚠️⚠️ `REGIONS` A ÉTÉ RETIRÉE — c'était une liste CODÉE EN DUR, et le seul
 * échelon que ce formulaire proposait. Les deux suivants — « Préfecture » et
 * « Commune / Sous-préfecture » — étaient des `<input>` libres, alors que
 * `ter.circonscription` porte 38 préfectures et 340 communes ou
 * sous-préfectures avec leur rattachement (mesuré le 24/09/2026, 0 orpheline
 * sous la région). Ce que la saisie libre a coûté est mesuré : le 23/09/2026,
 * un dépôt a déclaré « Conakry / Conakry / Matoto » pour une parcelle de
 * Boké-Centre, et l'affaire est partie à 250 km de son fonds.
 *
 * Les trois échelons viennent désormais de `ChampsAdresse`, en cascade. Le
 * quartier reste libre : le découpage de l'État s'arrête à la commune, et lui
 * proposer une liste en inventerait une.
 */

/**
 * L'adresse d'un bloc, lue et écrite dans le Record plat du formulaire.
 *
 * ⚠️ LE FORMULAIRE RESTE PLAT, ET C'EST DÉLIBÉRÉ. Sept blocs y demandent une
 * adresse (demandeur, deux parties, deux témoins, défunt, immeuble) sous sept
 * préfixes ; les imbriquer obligerait à réécrire `partie()`, `temoins()`,
 * `defunt()` et `construire()` — quatre fonctions que `personnePhysiqueSchema`
 * contrôle déjà champ par champ. On adapte à la frontière, on ne refond pas.
 */
const adresseDe = (f: Record<string, any>, p: string): Adresse => ({
  region: f[`${p}Region`] ?? '', prefecture: f[`${p}Prefecture`] ?? '',
  commune: f[`${p}Commune`] ?? '', quartier: f[`${p}Quartier`] ?? '',
});

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LE PARCOURS EN ÉTAPES — un cadre à la fois, et l'agent sait où il en est.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Demande du fondateur, 24/09/2026 : « la démarche repose sur 8 étapes du
 * demandeur à la déclaration de confidentialité (on peut naviguer entre les
 * étapes), l'agent sait à quelle étape il est, auto-enregistrement, le
 * brouillon est automatiquement enregistré ».
 *
 * ⚠️⚠️ LE NOMBRE D'ÉTAPES N'EST PAS UNE CONSTANTE, et le coder en dur serait
 * faux pour six types sur neuf. C'est le nombre de CADRES du type : donation,
 * vente, mainlevée et inscription de sûreté en portent 9 ; l'extrait cadastral
 * 5 ; le duplicata 3 ; la succession 6. Ce fichier a DÉJÀ payé une numérotation
 * écrite à la main — la donation portait deux cadres n° 7 et affichait son
 * cadre 8 avant le 7.
 *
 * ⚠️ LES CADRES S'INSCRIVENT EUX-MÊMES. Le rail est bâti par ce que le
 * formulaire RETOURNE réellement pour ce type, jamais par une table parallèle
 * de titres : une table se désynchronise au premier cadre ajouté, et le
 * désaccord se lit « une étape a disparu », ce que personne ne cherche.
 *
 * ⚠️ LES CADRES NON COURANTS RESTENT MONTÉS, cachés. Les démonter perdrait la
 * saisie à chaque changement d'étape — et l'inscription au rail avec.
 */
interface Parcours {
  etape: number;
  inscrire: (n: number, titre: string) => void;
}
const ParcoursCtx = createContext<Parcours | null>(null);

function Cadre({ n, titre, aide, children }: {
  n: number; titre: string; aide?: string; children: React.ReactNode;
}) {
  const p = useContext(ParcoursCtx);
  const inscrire = p?.inscrire;
  useEffect(() => { inscrire?.(n, titre); }, [inscrire, n, titre]);
  const cache = p !== null && p.etape !== n;
  return (
    <fieldset className="cadre" hidden={cache} data-etape={n}>
      <legend><span className="cadre-n">{n}</span> {titre}</legend>
      {aide && <p className="cadre-aide">{aide}</p>}
      <div className="cadre-corps">{children}</div>
    </fieldset>
  );
}

function Champ({ label, aide, obligatoire, children }: {
  label: string; aide?: string; obligatoire?: boolean; children: React.ReactNode;
}) {
  return (
    <label className="champ">
      <span className="champ-label">
        {label}{obligatoire && <b className="requis" aria-label="obligatoire">*</b>}
      </span>
      {children}
      {aide && <span className="champ-aide">{aide}</span>}
    </label>
  );
}

/**
 * UNE PARTIE À L'ACTE — donateur ou donataire.
 *
 * ⚠️ ÉCRIT UNE FOIS POUR DEUX CADRES. Une donation a deux parties, et recopier
 * les dix champs d'état civil aurait produit deux blocs qui divergent au
 * premier ajout — celui qu'on oublie de corriger étant toujours celui qui
 * reçoit les données du bénéficiaire.
 *
 * ⚠️ LES CHAMPS SONT CEUX QUE `personnePhysiqueSchema` EXIGE, pas ceux qu'on
 * suppose. Mesuré en écrivant les essais : une fixture qui inventait
 * `nomPere`, `pieceIdentite.type` et oubliait `telephone` faisait passer
 * QUATRE essais de refus pour la mauvaise raison. Un formulaire qui invente un
 * nom de champ produit le même symptôme, sauf qu'il le produit chez l'usager :
 * il remplit tout, et le dépôt rend 400.
 */
function CadrePartie({ prefixe, f, set, pose, aide, repris, onDelier, onRelier }: {
  prefixe: string; f: Record<string, any>;
  set: (k: string) => (e: React.ChangeEvent<any>) => void;
  /** Pose une valeur sans passer par un événement — pour les champs composés. */
  pose: (k: string, v: unknown) => void;
  aide: string;
  /** Rempli depuis le cadre 1 : l'agent doit le SAVOIR, pas le deviner. */
  repris?: boolean;
  onDelier?: () => void;
  onRelier?: () => void;
}) {
  const c = (k: string) => `${prefixe}${k}`;
  const morale = f[c('Qualite')] === 'personne_morale';
  return (
    <>
      <p className="cadre-aide">{aide}</p>
      {/* ⚠️ UNE REPRISE SILENCIEUSE SE LIT « le formulaire a rempli tout seul,
          donc je peux ne pas relire ». On la nomme, et on laisse délier : un
          requérant peut s'être trompé de case au cadre 1. */}
      {repris === true && (
        <p className="cadre-aide">
          <strong>Repris du cadre 1.</strong> Le demandeur s'est déclaré cette partie :
          son état civil n'est pas ressaisi, et il suit les corrections du cadre 1.{' '}
          <button type="button" className="lien" onClick={onDelier}>Délier</button>
        </p>
      )}
      {repris === false && (
        <p className="cadre-aide">
          <strong>Délié du cadre 1.</strong> Les deux fiches vivent séparément — un
          accent d'écart suffit à créer deux personnes au registre.{' '}
          <button type="button" className="lien" onClick={onRelier}>Relier</button>
        </p>
      )}
      <Champ label="Qualité" obligatoire>
        <select value={f[c('Qualite')] ?? 'personne_physique'} onChange={set(c('Qualite'))}>
          <option value="personne_physique">Personne physique</option>
          <option value="personne_morale">Personne morale</option>
        </select>
      </Champ>
      {morale ? (
        <>
          <div className="ligne-champs">
            <Champ label="Dénomination" obligatoire>
              <input value={f[c('Denomination')] ?? ''} onChange={set(c('Denomination'))} required />
            </Champ>
            <Champ label="Forme juridique" obligatoire>
              <input value={f[c('Forme')] ?? ''} onChange={set(c('Forme'))} placeholder="SARL, SA, GIE…" required />
            </Champ>
          </div>
          <div className="ligne-champs">
            <Champ label="Représentant — nom" obligatoire>
              <input value={f[c('RepNom')] ?? ''} onChange={set(c('RepNom'))} required />
            </Champ>
            <Champ label="Prénoms" obligatoire>
              <input value={f[c('RepPrenoms')] ?? ''} onChange={set(c('RepPrenoms'))} required />
            </Champ>
            <Champ label="Qualité du représentant" obligatoire>
              <input value={f[c('RepQualite')] ?? ''} onChange={set(c('RepQualite'))} required />
            </Champ>
          </div>
        </>
      ) : (
        <>
          <div className="ligne-champs">
            <Champ label="Nom" obligatoire>
              <SaisieMajuscule valeur={f[c('Nom')] ?? ''} onValeur={(v) => pose(c('Nom'), v)} required />
            </Champ>
            <Champ label="Prénoms" obligatoire>
              <SaisieMajuscule valeur={f[c('Prenoms')] ?? ''} onValeur={(v) => pose(c('Prenoms'), v)} required />
            </Champ>
          </div>

          {/* ⚠️⚠️ LE NIN EST DEVENU OBLIGATOIRE, ET C'EST UN RENVERSEMENT.
              Il était facultatif par décision — « l'essentiel du foncier
              guinéen est détenu par des personnes qui n'en ont pas ». Le
              fondateur a tranché l'inverse le 24/09/2026. Ce qui change
              vraiment : il n'est plus un `<input>` nu. La règle de
              `fon.nin_valide` (clé 7-3-1) et le décodage de `fon.decoder_nin`
              (sexe, date de naissance) existaient en base depuis la migration
              018 et cet écran ne s'en servait pas — Zod acceptait six
              caractères, et le refus tombait plus tard, en SQL brut, quand
              l'usager était reparti. */}
          <Champ label="Identifiant national (NIN)" obligatoire
                 aide="15 chiffres. Il porte déjà le sexe et la date de naissance — c'est lui qui les vérifie.">
            <ChampNin valeur={f[c('Nin')] ?? ''} onValeur={(v) => pose(c('Nin'), v)}
                      dateNaissance={f[c('Naissance')]}
                      onDateDeduite={(iso) => pose(c('Naissance'), iso)}
                      lieuNaissance={f[c('LieuNaissance')]}
                      onLieuDeduit={(nom) => pose(c('LieuNaissance'), nom)} />
          </Champ>

          <div className="ligne-champs">
            <Champ label="Date de naissance" obligatoire>
              <input type="date" value={f[c('Naissance')] ?? ''} onChange={set(c('Naissance'))} required />
            </Champ>
            <Champ label="Lieu de naissance" obligatoire>
              <SaisieMajuscule valeur={f[c('LieuNaissance')] ?? ''}
                               onValeur={(v) => pose(c('LieuNaissance'), v)} required />
            </Champ>
            {/* ⚠️⚠️ CE CHAMP N'EXISTAIT PAS, ET LE CODE ÉCRIVAIT « Guinéenne »
                EN DUR pour les deux parties de tout acte. Un donateur étranger
                était inscrit guinéen, en silence, sur un acte foncier. */}
            <Champ label="Nationalité" obligatoire>
              <ChampNationalite valeur={f[c('Nationalite')] ?? ''}
                                onValeur={(v) => pose(c('Nationalite'), v)} requis />
            </Champ>
          </div>
          <div className="ligne-champs">
            <Champ label="Pièce d'identité" obligatoire>
              <select value={f[c('PieceType')] ?? PIECE_PAR_DEFAUT} onChange={set(c('PieceType'))}>
                {PIECES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Champ>
            <Champ label="Numéro" obligatoire>
              <input value={f[c('PieceNumero')] ?? ''} onChange={set(c('PieceNumero'))} required />
            </Champ>
            <Champ label="Pièce délivrée le">
              <input type="date" value={f[c('PieceDelivreeLe')] ?? ''} onChange={set(c('PieceDelivreeLe'))} />
            </Champ>
          </div>
          <div className="ligne-champs">
            <Champ label="Profession">
              <SaisieMajuscule valeur={f[c('Profession')] ?? ''} onValeur={(v) => pose(c('Profession'), v)} />
            </Champ>
            {/* ⚠️ LE COURRIEL EST LE SEUL CHAMP ÉPARGNÉ PAR LES CAPITALES, et
                on le dit : la partie locale d'une adresse est sensible à la
                casse, et la mettre en majuscules peut rendre le récépissé
                indélivrable. */}
            <Champ label="Courriel" aide="seul champ non mis en capitales — une adresse est sensible à la casse">
              <input type="email" value={f[c('Courriel')] ?? ''} onChange={set(c('Courriel'))} />
            </Champ>
          </div>

          {/* ⚠️ LA FILIATION S'IDENTIFIE, ELLE NE SE NOMME PAS. « DIALLO Mamadou »
              en une seule case ne départage pas deux homonymes — et c'est
              exactement ce qu'on lui demande sur un acte foncier. La date de
              naissance d'un parent reste FACULTATIVE : l'enregistrement des
              naissances n'est pas universel, et l'exiger fermerait le guichet à
              ceux dont les parents sont nés avant l'état civil généralisé. */}
          <fieldset className="sous-cadre">
            <legend>Filiation — pour lever les homonymies</legend>
            {(['Pere', 'Mere'] as const).map((lien) => (
              <div key={lien}>
                <div className="ligne-champs">
                  <Champ label={`${lien === 'Pere' ? 'Père' : 'Mère'} — nom`}>
                    <SaisieMajuscule valeur={f[c(`${lien}Nom`)] ?? ''} onValeur={(v) => pose(c(`${lien}Nom`), v)} />
                  </Champ>
                  <Champ label="Prénoms">
                    <SaisieMajuscule valeur={f[c(`${lien}Prenoms`)] ?? ''} onValeur={(v) => pose(c(`${lien}Prenoms`), v)} />
                  </Champ>
                  <Champ label="Né(e) le" aide="si connue">
                    <input type="date" value={f[c(`${lien}Naissance`)] ?? ''} onChange={set(c(`${lien}Naissance`))} />
                  </Champ>
                </div>
                <div className="ligne-champs">
                  <Champ label="Lieu de naissance">
                    <SaisieMajuscule valeur={f[c(`${lien}LieuNaissance`)] ?? ''}
                                     onValeur={(v) => pose(c(`${lien}LieuNaissance`), v)} />
                  </Champ>
                  <Champ label="Nationalité">
                    <ChampNationalite valeur={f[c(`${lien}Nationalite`)] ?? ''}
                                      onValeur={(v) => pose(c(`${lien}Nationalite`), v)} />
                  </Champ>
                  <Champ label="Profession">
                    <SaisieMajuscule valeur={f[c(`${lien}Profession`)] ?? ''}
                                     onValeur={(v) => pose(c(`${lien}Profession`), v)} />
                  </Champ>
                  {/* ⚠️ TROIS ÉTATS, PAS DEUX : « on ne sait pas » n'est pas
                      « vivant », et l'acte peut servir dans une succession. */}
                  <Champ label="Statut">
                    <select value={f[c(`${lien}Statut`)] ?? ''} onChange={set(c(`${lien}Statut`))}>
                      <option value="">Non déclaré</option>
                      <option value="vivant">Vivant(e)</option>
                      <option value="decede">Décédé(e)</option>
                    </select>
                  </Champ>
                </div>
              </div>
            ))}
          </fieldset>

          {/* ⚠️ LE RÉGIME CONDITIONNE LA VALIDITÉ DE LA DONATION. Selon lui, le
              consentement du conjoint est requis ; un acte qui tait le régime
              laisse l'autorité réceptrice décider sans savoir, et un époux lésé
              le découvre après coup. */}
          <fieldset className="sous-cadre">
            <legend>Situation matrimoniale — elle peut conditionner la validité de l'acte</legend>
            <div className="ligne-champs">
              <Champ label="Situation" obligatoire>
                <select value={f[c('Situation')] ?? 'celibataire'} onChange={set(c('Situation'))}>
                  <option value="celibataire">Célibataire</option>
                  <option value="marie">Marié(e)</option>
                  <option value="veuf">Veuf / Veuve</option>
                  <option value="divorce">Divorcé(e)</option>
                  <option value="separe">Séparé(e)</option>
                </select>
              </Champ>
              {f[c('Situation')] === 'marie' && (
                <>
                  <Champ label="Régime matrimonial" obligatoire>
                    <select value={f[c('Regime')] ?? 'inconnu'} onChange={set(c('Regime'))}>
                      <option value="communaute_des_biens">Communauté des biens</option>
                      <option value="separation_des_biens">Séparation des biens</option>
                      <option value="polygamie_declaree">Polygamie déclarée</option>
                      <option value="coutumier">Régime coutumier</option>
                      <option value="inconnu">Non déterminé</option>
                    </select>
                  </Champ>
                  <Champ label="Nom du conjoint">
                    <SaisieMajuscule valeur={f[c('ConjointNom')] ?? ''} onValeur={(v) => pose(c('ConjointNom'), v)} />
                  </Champ>
                </>
              )}
            </div>
            {f[c('Situation')] === 'marie' && (
              <label className="case">
                <input type="checkbox" checked={f[c('ConjointConsent')] === true}
                       onChange={(e) => set(c('ConjointConsent'))({ target: { value: e.target.checked } } as any)} />
                Le conjoint <strong>consent</strong> à la donation.
                Laissé décoché, l'acte portera « consentement non recueilli » — ce qui n'est pas un refus,
                mais ce que l'autorité réceptrice devra lever avant de recevoir.
              </label>
            )}
          </fieldset>

          {/* ⚠️ OBLIGATOIRE POUR UNE PARTIE — la règle existe déjà au registre
              pour l'attestation de cession, dont la délivrance est REFUSÉE
              quand la photo du cédant ou de l'acquéreur manque (068). */}
          <ChampPortrait qui={QUI_EST_CE[prefixe] ?? 'la partie'} obligatoire
                         valeur={f[c('Portrait')] ?? ''} onValeur={(v) => pose(c('Portrait'), v)} />
        </>
      )}
      <div className="ligne-champs">
        <Champ label="Téléphone" obligatoire>
          {/* ⚠️ PAS DE CAPITALES SUR UN NUMÉRO : il n'en a pas, et les y passer
              ne ferait que masquer un caractère parasite. */}
          <input value={f[c('Telephone')] ?? ''} onChange={set(c('Telephone'))} placeholder="+224…" required />
        </Champ>
        {/* ⚠️ TROIS ÉCHELONS EN CASCADE, tirés de `ter.circonscription`. Ils
            étaient : un menu de 8 régions codé en dur, puis DEUX champs libres. */}
        <ChampsAdresse requis adresse={adresseDe(f, prefixe)}
                       onAdresse={(a) => {
                         pose(c('Region'), a.region); pose(c('Prefecture'), a.prefecture);
                         pose(c('Commune'), a.commune); pose(c('Quartier'), a.quartier ?? '');
                       }} />
      </div>
    </>
  );
}

/**
 * D'OÙ LE CÉDANT TIENT L'IMMEUBLE — écrit UNE fois pour la donation ET la vente.
 *
 * ⚠️⚠️ CES DIX CHAMPS ÉTAIENT ÉCRITS EN LIGNE DANS LE CADRE DE LA DONATION, et
 * la vente en a besoin mot pour mot (`originePropriete` est le même schéma pour
 * les deux). Les recopier aurait donné deux cadres qui divergent au premier
 * champ ajouté — et celui qu'on oublie de corriger est toujours l'autre. C'est
 * le motif que `identiteComparable` a déjà coûté dans `formulaires.ts`, et il
 * se paie toujours sur un bloc « trop simple pour mériter un composant ».
 *
 * ⚠️ SEUL LE MODE EST OBLIGATOIRE. Un héritier ne connaît pas toujours le
 * numéro de l'acte par lequel son père a acquis ; l'exiger fermerait le guichet
 * à la transmission familiale, qui est le cas le plus fréquent.
 */
function CadreOrigine({ f, set }: {
  f: Record<string, any>;
  set: (k: string) => (e: React.ChangeEvent<any>) => void;
}) {
  return (
    <>
          <div className="ligne-champs">
            <Champ label="Mode de détention" obligatoire>
              <select value={f.origMode ?? 'heritage'} onChange={set('origMode')}>
                <option value="titre_foncier">Titre foncier</option>
                <option value="achat">Achat</option>
                <option value="heritage">Héritage</option>
                <option value="donation_anterieure">Donation antérieure</option>
                <option value="partage">Partage</option>
                <option value="attribution_administrative">Attribution administrative</option>
                <option value="attribution_coutumiere">Attribution coutumière</option>
                <option value="occupation_prolongee">Occupation prolongée</option>
                <option value="autre">Autre</option>
              </select>
            </Champ>
            <Champ label="Détient depuis le"><input type="date" value={f.origDepuis ?? ''} onChange={set('origDepuis')} /></Champ>
            <Champ label="Précédent propriétaire"><input value={f.origPrecedent ?? ''} onChange={set('origPrecedent')} /></Champ>
          </div>
          <div className="ligne-champs">
            <Champ label="Nature de l'acte d'origine" aide="acte de vente, jugement, attestation…">
              <input value={f.origActeNature ?? ''} onChange={set('origActeNature')} />
            </Champ>
            <Champ label="Numéro de l'acte"><input value={f.origActeNumero ?? ''} onChange={set('origActeNumero')} /></Champ>
            <Champ label="Date de l'acte"><input type="date" value={f.origActeDate ?? ''} onChange={set('origActeDate')} /></Champ>
          </div>
          <div className="ligne-champs">
            <Champ label="Autorité ayant reçu l'acte"><input value={f.origActeAutorite ?? ''} onChange={set('origActeAutorite')} /></Champ>
            <Champ label="Référence d'inscription" aide="au livre foncier, si elle existe">
              <input value={f.origInscription ?? ''} onChange={set('origInscription')} />
            </Champ>
          </div>
          <Champ label="Précisions">
            <textarea rows={2} value={f.origDetail ?? ''} onChange={set('origDetail')} />
          </Champ>
    </>
  );
}

/**
 * LES TÉMOINS — écrit UNE fois pour la donation, la vente et la mainlevée.
 *
 * ⚠️ FACULTATIFS AU SCHÉMA, EXIGÉS PAR L'AUTORITÉ QUI REÇOIT. Le registre ne
 * peut pas savoir si la commune du lieu en impose ; il refuse donc de bloquer
 * un dépôt pour une règle qu'il ignore, et l'acte imprime l'absence plutôt que
 * de la taire. Un cadre laissé vide n'est pas envoyé.
 */
function CadreTemoins({ f, set, pose }: {
  f: Record<string, any>;
  set: (k: string) => (e: React.ChangeEvent<any>) => void;
  pose: (k: string, v: unknown) => void;
}) {
  return (
    <>
          {(['t1', 't2'] as const).map((prefixe, i) => (
            <fieldset key={prefixe} className="sous-cadre">
              <legend>Témoin n° {i + 1}</legend>
              {/* ⚠️⚠️ QUATRE CHAMPS ÉTAIENT LUS ET JAMAIS AFFICHÉS. `temoins()`
                  lit `Nin`, `Nationalite`, `Region` et `PieceDelivreeLe` — et
                  cet écran n'en montrait AUCUN : quatre lectures mortes, donc
                  quatre champs du schéma que le registre ne recevait jamais.
                  Pire, l'adresse retombait sur « Conakry » à trois échelons
                  quand elle était vide : le registre inscrivait un domicile
                  INVENTÉ pour un témoin dont on ne savait rien. */}
              <div className="ligne-champs">
                <Champ label="Nom">
                  <SaisieMajuscule valeur={f[`${prefixe}Nom`] ?? ''} onValeur={(v) => pose(`${prefixe}Nom`, v)} />
                </Champ>
                <Champ label="Prénoms">
                  <SaisieMajuscule valeur={f[`${prefixe}Prenoms`] ?? ''} onValeur={(v) => pose(`${prefixe}Prenoms`, v)} />
                </Champ>
                <Champ label="Né(e) le"><input type="date" value={f[`${prefixe}Naissance`] ?? ''} onChange={set(`${prefixe}Naissance`)} /></Champ>
                <Champ label="Lieu de naissance">
                  <SaisieMajuscule valeur={f[`${prefixe}LieuNaissance`] ?? ''} onValeur={(v) => pose(`${prefixe}LieuNaissance`, v)} />
                </Champ>
              </div>
              <Champ label="Identifiant national (NIN) du témoin"
                     aide="le témoin atteste une identité : la sienne doit être établie comme celle des parties">
                <ChampNin valeur={f[`${prefixe}Nin`] ?? ''} onValeur={(v) => pose(`${prefixe}Nin`, v)}
                          requis={false}
                          dateNaissance={f[`${prefixe}Naissance`]}
                          onDateDeduite={(iso) => pose(`${prefixe}Naissance`, iso)}
                          lieuNaissance={f[`${prefixe}LieuNaissance`]}
                          onLieuDeduit={(nom) => pose(`${prefixe}LieuNaissance`, nom)} />
              </Champ>
              <div className="ligne-champs">
                <Champ label="Nationalité">
                  <ChampNationalite valeur={f[`${prefixe}Nationalite`] ?? ''}
                                    onValeur={(v) => pose(`${prefixe}Nationalite`, v)} />
                </Champ>
                <Champ label="Profession">
                  <SaisieMajuscule valeur={f[`${prefixe}Profession`] ?? ''} onValeur={(v) => pose(`${prefixe}Profession`, v)} />
                </Champ>
                <Champ label="Pièce d'identité">
                  <select value={f[`${prefixe}PieceType`] ?? PIECE_PAR_DEFAUT} onChange={set(`${prefixe}PieceType`)}>
                    {PIECES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </Champ>
                <Champ label="Numéro de la pièce">
                  <SaisieMajuscule valeur={f[`${prefixe}PieceNumero`] ?? ''} onValeur={(v) => pose(`${prefixe}PieceNumero`, v)} />
                </Champ>
                <Champ label="Délivrée le">
                  <input type="date" value={f[`${prefixe}PieceDelivreeLe`] ?? ''} onChange={set(`${prefixe}PieceDelivreeLe`)} />
                </Champ>
              </div>
              <ChampPortrait qui={`témoin n° ${i + 1}`}
                             valeur={f[`${prefixe}Portrait`] ?? ''}
                             onValeur={(v) => pose(`${prefixe}Portrait`, v)} />
              <div className="ligne-champs">
                <ChampsAdresse adresse={adresseDe(f, prefixe)}
                               onAdresse={(a) => {
                                 pose(`${prefixe}Region`, a.region); pose(`${prefixe}Prefecture`, a.prefecture);
                                 pose(`${prefixe}Commune`, a.commune); pose(`${prefixe}Quartier`, a.quartier ?? '');
                               }} />
                <Champ label="Téléphone"><input value={f[`${prefixe}Telephone`] ?? ''} onChange={set(`${prefixe}Telephone`)} placeholder="+224…" /></Champ>
              </div>
              <div className="ligne-champs">
                {/* ⚠️ UN LIEN ÉTROIT N'INTERDIT PAS DE TÉMOIGNER, mais il doit
                    être connu de l'autorité : le taire vaudrait dissimulation.
                    Il était libre — donc vide neuf fois sur dix, ce qui revient
                    au même que le taire. */}
                <Champ label="Lien avec les parties" obligatoire={Boolean(f[`${prefixe}Nom`])}
                       aide="il se déclare, il ne se devine pas">
                  <select value={f[`${prefixe}Lien`] ?? ''} onChange={set(`${prefixe}Lien`)}>
                    <option value="">— à déclarer —</option>
                    {LIENS_TEMOIN.map((l) => <option key={l} value={l}>{l}</option>)}
                    <option value={LIEN_AUTRE}>{LIEN_AUTRE}</option>
                  </select>
                </Champ>
                {f[`${prefixe}Lien`] === LIEN_AUTRE && (
                  <Champ label="Préciser le lien" obligatoire>
                    <SaisieMajuscule valeur={f[`${prefixe}LienAutre`] ?? ''}
                                     onValeur={(v) => pose(`${prefixe}LienAutre`, v)} required />
                  </Champ>
                )}
              </div>
            </fieldset>
          ))}
    </>
  );
}

function FormulaireDemande(
  { type, onFerme, onDepose, parcelleDesignee, onParcelleConsommee }:
  {
    type: TypeDemande; onFerme: () => void; onDepose: (reference: string) => void;
    /** Désignée sur la carte (§2.3) — ce que le registre sait n'est pas retapé. */
    parcelleDesignee?: string | null;
    onParcelleConsommee?: () => void;
  },
) {
  const [qualite, setQualite] = useState<'personne_physique' | 'personne_morale'>('personne_physique');
  const [agitPour, setAgitPour] = useState<'soi_meme' | 'tiers'>('soi_meme');
  /**
   * ⚠️ LE DEMANDEUR DÉCLARE S'IL EST PARTIE. Demande du fondateur, 24/09/2026 :
   * « on doit demander si le demandeur est le donataire ou le donateur
   * (remplissage automatique des infos déjà saisies) ».
   */
  const [estPartie, setEstPartie] = useState<'cedant' | 'beneficiaire' | null>(null);
  /** ⚠️ Délier reste possible : un requérant peut se tromper de case. */
  const [delie, setDelie] = useState(false);
  const [f, setF] = useState<Record<string, any>>({
    nationalite: 'Guinéenne', pieceType: PIECE_PAR_DEFAUT,
    region: 'Conakry', objet: 'releve_biens',
    immRegion: 'Conakry',
  });
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setF((x) => ({ ...x, [k]: e.target.value }));
  /**
   * Poser une valeur sans passer par un événement.
   *
   * ⚠️ LES CHAMPS COMPOSÉS N'ONT PAS D'ÉVÉNEMENT À DONNER. Une adresse en
   * cascade écrit quatre clés d'un coup, un NIN en écrit deux (le numéro et la
   * date qu'il déduit). Les faire passer par `set` obligeait à fabriquer de
   * faux `ChangeEvent` — ce que ce fichier faisait déjà une fois pour une case
   * à cocher, avec un `as any` qui muselait le typage.
   */
  const pose = useCallback((k: string, v: unknown) => setF((x) => ({ ...x, [k]: v })), []);

  /**
   * ═══════════════════════════════════════════════════════════════════════
   * ⚠️⚠️ CE QUE LE REGISTRE SAIT NE SE RETAPE PAS — §2.3
   * ═══════════════════════════════════════════════════════════════════════
   *
   * « Une affaire naît d'une parcelle, DÉSIGNÉE SUR LA CARTE — jamais en
   * tapant un identifiant. C'est le seul moyen de voir, AVANT d'ouvrir, ce qui
   * pèse déjà sur ce fonds. »
   *
   * ⚠️ CE QUE LA SAISIE LIBRE A COÛTÉ, MESURÉ. Ce formulaire demandait
   * l'identifiant « si le requérant le connaît » et laissait l'adresse libre,
   * avec `immRegion: 'Conakry'` EN DUR comme valeur de départ. Le 23/09/2026,
   * un dépôt a déclaré « Conakry / Conakry / Matoto / Tombola » pour la
   * parcelle GN-PAR-000000004, qui est à **Boké-Centre, quartier Yomboya**.
   * Aucun contrôle ne l'a vu : l'affaire est partie à la Commune de Matoto, à
   * 250 km du fonds. Seule la RÉFÉRENCE l'a rendu visible — `AFF-MTO-…` sur un
   * fonds de Boké.
   *
   * ⚠️ ET L'IDENTITÉ DES PARTIES N'EST PAS PRÉ-REMPLIE. Le registre connaît le
   * TITULAIRE INSCRIT — fait opposable, proposé comme cédant ou donateur. Il ne
   * connaît ni l'acquéreur ni le donataire : les inventer serait pire que les
   * demander.
   */
  const [preRemplissage, setPreRemplissage] = useState<PreRemplissageDemande | null>(null);
  const [preErreur, setPreErreur] = useState<string | null>(null);

  useEffect(() => {
    if (!parcelleDesignee) return;
    let vivant = true;
    void (async () => {
      try {
        const p = await api.parcelles.pourDemande(parcelleDesignee);
        if (!vivant) return;
        setPreRemplissage(p);
        setPreErreur(null);
        setF((x) => ({
          ...x,
          immIdentifiant: p.immeuble.identifiantParcelle,
          immRegion: p.immeuble.region ?? x.immRegion,
          immPrefecture: p.immeuble.prefecture ?? x.immPrefecture,
          immCommune: p.immeuble.communeOuSousPrefecture ?? x.immCommune,
          immQuartier: p.immeuble.quartierOuDistrict ?? x.immQuartier,
          immReference: p.immeuble.referenceCadastrale ?? x.immReference,
          immLieuDit: p.immeuble.lieuDit ?? x.immLieuDit,
        }));
      } catch (e) {
        /* ⚠️ ON NE TAIT PAS L'ÉCHEC : sans cette phrase, l'agent verrait un
           formulaire vide et conclurait que le registre ne sait rien — alors
           qu'il n'a pas pu être interrogé. Les deux états sont opposés. */
        if (vivant) setPreErreur(e instanceof ErreurApi ? e.message
          : "Le registre n'a pas pu être interrogé sur cette parcelle : saisissez l'immeuble à la main.");
      } finally {
        if (vivant) onParcelleConsommee?.();
      }
    })();
    return () => { vivant = false; };
  }, [parcelleDesignee, onParcelleConsommee]);

  /**
   * ⚠️ LE NOMBRE DE CADRES D'HÉRITIER EST OUVERT, ET IL COMMENCE À UN. Une
   * succession en compte de un à une vingtaine ; poser d'emblée six cadres
   * vides ferait croire qu'on en attend six, et un cadre vide se lit « il
   * manque quelqu'un ». Le schéma en admet trente au plus.
   */
  /**
   * LA RECOPIE DU CADRE 1 VERS LE CADRE DE LA PARTIE.
   *
   * ⚠️ VIVANTE, PAS UNE COPIE UNIQUE. Un agent corrige souvent le cadre 1 après
   * l'avoir rempli — une lettre du nom, le numéro de pièce. Une copie faite au
   * moment du choix laisserait les deux fiches diverger sans que rien ne le
   * montre, et le registre y verrait deux personnes. C'est exactement le défaut
   * qu'on ferme.
   *
   * ⚠️ ET ELLE NE S'APPLIQUE QU'À UNE PERSONNE PHYSIQUE. Une personne morale
   * n'a ni date de naissance ni NIN : recopier ses champs poserait des clés
   * vides dans un cadre qui en attend d'autres.
   */
  const parties = PARTIES_DU_TYPE[type.code];
  const prefixePartie = estPartie && parties ? parties[estPartie][0] : null;
  useEffect(() => {
    if (!prefixePartie || delie || qualite !== 'personne_physique') return;
    setF((x) => {
      const y = { ...x };
      for (const [cle, suffixe] of MIROIR_DEMANDEUR) y[`${prefixePartie}${suffixe}`] = x[cle] ?? '';
      return y;
    });
  }, [prefixePartie, delie, qualite,
      // ⚠️ La liste des dépendances EST la liste des champs recopiés : en
      //    oublier un ferait une recopie qui s'arrête au premier correctif.
      ...MIROIR_DEMANDEUR.map(([cle]) => f[cle])]);

  const [nbHeritiers, setNbHeritiers] = useState(1);
  const [engagement, setEngagement] = useState(false);
  const [declaration, setDeclaration] = useState(false);
  /** ⚠️ L'ACCEPTATION DU DONATAIRE est constitutive de la donation : sans elle,
   *  il n'y a qu'une offre, et le service refuse le dépôt. */
  const [acceptation, setAcceptation] = useState(false);

  /**
   * Un parent, tel que `parentSchema` l'attend — ou `undefined` s'il n'est pas
   * renseigné.
   *
   * ⚠️ NOM ET PRÉNOMS COMMANDENT. Envoyer un objet ne portant qu'une date de
   * naissance ferait échouer la validation Zod avec un message que l'agent ne
   * pourrait pas relier à ce qu'il a saisi.
   */
  function parent(prefixe: string, lien: 'Pere' | 'Mere') {
    const g = (k: string) => f[`${prefixe}${lien}${k}`];
    const nom = g('Nom')?.trim();
    const prenoms = g('Prenoms')?.trim();
    if (!nom || !prenoms) return undefined;
    const statut = g('Statut');
    return {
      nom, prenoms,
      dateNaissance: g('Naissance') || undefined,
      lieuNaissance: g('LieuNaissance')?.trim() || undefined,
      nationalite: g('Nationalite')?.trim() || undefined,
      profession: g('Profession')?.trim() || undefined,
      decede: statut === 'decede' ? true : statut === 'vivant' ? false : undefined,
    };
  }

  /**
   * LE DÉFUNT, TEL QUE `defuntSchema` L'ATTEND.
   *
   * ⚠️⚠️ CE N'EST PAS UNE `personnePhysiqueSchema`, ET C'EST TOUT L'INTÉRÊT.
   * Celle-ci exige un TÉLÉPHONE, une PIÈCE D'IDENTITÉ et son NUMÉRO : réclamés
   * au guichet pour un mort, ces trois champs ferment la porte au cas le plus
   * fréquent du foncier guinéen — l'héritier qui vient déclarer le décès de son
   * père n'a ni sa carte, ni son numéro, et son père n'a plus de téléphone.
   *
   * ⚠️ `decede: true` EST ENVOYÉ, ET IL EST LU À L'IMPRESSION. Sans lui, l'acte
   * de dévolution réclamerait en surbrillance le téléphone et le courriel du
   * défunt — « champ non renseigné au guichet », ce qui est faux : il ne PEUT
   * pas l'être. « Sans objet » et « à compléter » n'appellent pas le même geste.
   *
   * ⚠️ L'ADRESSE EST LE DERNIER DOMICILE. C'est le seul champ de ce cadre qu'un
   * mort possède encore, et c'est lui qui rattache la succession à un ressort.
   */
  function defunt() {
    return {
      nom: f.dcNom?.trim(), prenoms: f.dcPrenoms?.trim(),
      dateNaissance: f.dcNaissance || undefined,
      lieuNaissance: f.dcLieuNaissance?.trim() || undefined,
      nationalite: f.dcNationalite?.trim() || 'Guinéenne',
      profession: f.dcProfession?.trim() || undefined,
      pere: parent('dc', 'Pere'), mere: parent('dc', 'Mere'),
      pieceType: f.dcPieceType || undefined,
      pieceNumero: f.dcPieceNumero?.trim() || undefined,
      nin: f.dcNin?.trim() || undefined,
      situationMatrimoniale: f.dcSituation || undefined,
      conjointNom: f.dcConjointNom?.trim() || undefined,
      adresse: {
        /* ⚠️ PLUS DE REPLI SUR « Conakry ». Le dernier domicile RATTACHE LA
           SUCCESSION À UN RESSORT : en inventer un enverrait l'affaire au
           mauvais service, et rien ne permettrait de le voir. Vide, le schéma
           refuse — ce qui est le comportement juste. */
        region: f.dcRegion,
        prefecture: f.dcPrefecture?.trim(),
        communeOuSousPrefecture: f.dcCommune?.trim(),
        quartierOuDistrict: f.dcQuartier?.trim() || undefined,
      },
      decede: true as const,
    };
  }

  /**
   * LES AYANTS DROIT DÉCLARÉS — ceux qui sont réellement saisis, dans l'ordre.
   *
   * ⚠️ ON N'ENVOIE PAS DE CADRE VIDE, même motif que les témoins : une ligne
   * sans nom déposerait un héritier que l'instruction devrait ensuite écarter,
   * alors que la vérité est que l'agent n'a pas rempli le cadre.
   *
   * ⚠️ LA VOCATION N'EST ENVOYÉE QU'ENTIÈRE. Un numérateur sans dénominateur
   * inscrirait une part qu'on ne sait pas lire, et le schéma la refuse — en
   * nommant un champ que l'agent croit avoir rempli.
   */
  function heritiers() {
    return Array.from({ length: nbHeritiers }, (_, i) => `h${i}`).flatMap((prefixe) => {
      const g = (k: string) => f[`${prefixe}${k}`];
      const nom = g('Nom')?.trim();
      const prenoms = g('Prenoms')?.trim();
      if (!nom || !prenoms) return [];
      const num = g('Num'); const den = g('Den');
      const avecPart = num !== undefined && num !== '' && den !== undefined && den !== '';
      return [{
        nom, prenoms,
        dateNaissance: g('Naissance') || undefined,
        lieuNaissance: g('LieuNaissance')?.trim() || undefined,
        qualite: g('Qualite') ?? 'descendant',
        lien: g('Lien')?.trim() || undefined,
        ...(avecPart ? { vocationNum: Number(num), vocationDen: Number(den) } : {}),
        pieceType: g('PieceType') || undefined,
        pieceNumero: g('PieceNumero')?.trim() || undefined,
        nin: g('Nin')?.trim() || undefined,
        telephone: g('Telephone')?.trim() || undefined,
      }];
    });
  }

  /**
   * D'OÙ LE CÉDANT TIENT L'IMMEUBLE — écrit UNE fois pour la donation ET la
   * vente, comme le cadre qui le saisit.
   *
   * ⚠️ SANS ORIGINE, L'ACTE NOMME UN CÉDANT SANS POUVOIR DIRE À QUEL TITRE IL
   * CÈDE — et c'est précisément ce qu'une contestation attaque. Seul le MODE
   * est exigé : un héritier ne connaît pas toujours le numéro de l'acte par
   * lequel son père a acquis, et l'exiger fermerait le guichet à la
   * transmission familiale, qui est le cas le plus fréquent.
   */
  function origine() {
    return {
      mode: f.origMode ?? 'heritage',
      detail: f.origDetail?.trim() || undefined,
      detientDepuis: f.origDepuis || undefined,
      acteNature: f.origActeNature?.trim() || undefined,
      acteNumero: f.origActeNumero?.trim() || undefined,
      acteDate: f.origActeDate || undefined,
      acteAutorite: f.origActeAutorite?.trim() || undefined,
      precedentProprietaire: f.origPrecedent?.trim() || undefined,
      referenceInscription: f.origInscription?.trim() || undefined,
    };
  }

  /**
   * Les témoins réellement saisis, dans l'ordre.
   *
   * ⚠️ ON N'ENVOIE PAS DE CADRE VIDE. Un témoin sans nom déposerait une ligne
   * que l'acte imprimerait comme « déclaré sans identité — dépôt anormal »,
   * alors que la vérité est que l'agent n'a pas rempli le second cadre.
   */
  function temoins() {
    return (['t1', 't2'] as const).flatMap((prefixe) => {
      const g = (k: string) => f[`${prefixe}${k}`];
      const nom = g('Nom')?.trim();
      const prenoms = g('Prenoms')?.trim();
      const pieceNumero = g('PieceNumero')?.trim();
      const telephone = g('Telephone')?.trim();
      if (!nom || !prenoms || !pieceNumero || !telephone) return [];
      return [{
        nom, prenoms,
        dateNaissance: g('Naissance') || undefined,
        lieuNaissance: g('LieuNaissance')?.trim() || undefined,
        nationalite: g('Nationalite')?.trim() || undefined,
        profession: g('Profession')?.trim() || undefined,
        pieceType: g('PieceType') ?? PIECE_PAR_DEFAUT,
        pieceNumero,
        pieceDelivreeLe: g('PieceDelivreeLe') || undefined,
        nin: g('Nin')?.trim() || undefined,
        portraitPresent: g('Portrait') ? true : undefined,
        telephone,
        /**
         * ⚠️⚠️ TROIS REPLIS SUR « Conakry » ONT ÉTÉ RETIRÉS. Un témoin dont
         * l'écran ne demandait pas l'adresse — il n'affichait que préfecture et
         * commune — partait au registre domicilié « Conakry / Conakry /
         * Conakry ». Ce n'est pas une valeur par défaut, c'est un domicile
         * INVENTÉ sur un acte d'État, et rien ne permettait de le distinguer
         * d'un vrai. Le cadre demande maintenant les trois échelons ; s'ils
         * manquent, le schéma refuse le dépôt — ce qui est le comportement
         * juste : un témoin sans domicile n'est pas un témoin identifié.
         */
        adresse: {
          region: g('Region')?.trim(),
          prefecture: g('Prefecture')?.trim(),
          communeOuSousPrefecture: g('Commune')?.trim(),
          quartierOuDistrict: g('Quartier')?.trim() || undefined,
        },
        /* Le lien « autre » n'est pas un lien : c'est la promesse d'en nommer un. */
        lienAvecLesParties: (g('Lien') === LIEN_AUTRE ? g('LienAutre')?.trim() : g('Lien')?.trim()) || undefined,
      }];
    });
  }

  /** Une partie à l'acte, telle que `personnePhysiqueSchema` l'attend. */
  function partie(prefixe: string) {
    const c = (k: string) => f[`${prefixe}${k}`];
    const adresse = {
      /* ⚠️ MÊME MOTIF QUE LE TÉMOIN ET LE DÉFUNT : un domicile par défaut est
         un domicile INVENTÉ, indiscernable d'un vrai une fois au registre. */
      region: c('Region'),
      prefecture: c('Prefecture')?.trim(),
      communeOuSousPrefecture: c('Commune')?.trim(),
    };
    return c('Qualite') === 'personne_morale'
      ? {
          qualite: 'personne_morale' as const,
          personne: {
            denomination: c('Denomination')?.trim(), formeJuridique: c('Forme')?.trim(),
            siegeAdresse: adresse,
            representantNom: c('RepNom')?.trim(), representantPrenoms: c('RepPrenoms')?.trim(),
            representantQualite: c('RepQualite')?.trim(),
            telephone: c('Telephone')?.trim(),
          },
        }
      : {
          qualite: 'personne_physique' as const,
          personne: {
            nom: c('Nom')?.trim(), prenoms: c('Prenoms')?.trim(),
            dateNaissance: c('Naissance'), lieuNaissance: c('LieuNaissance')?.trim(),
            /**
             * ⚠️⚠️ ELLE ÉTAIT ÉCRITE EN DUR — `nationalite: 'Guinéenne'` — pour
             * les DEUX parties de tout acte, et le cadre ne la demandait pas.
             * Un donateur étranger était donc inscrit guinéen au registre, en
             * silence, sur un acte foncier. Le repli reste `'Guinéenne'`
             * parce que le schéma l'impose par défaut et que c'est le cas de
             * l'immense majorité ; ce qui change, c'est qu'on la DEMANDE.
             */
            nationalite: c('Nationalite')?.trim() || 'Guinéenne',
            profession: c('Profession')?.trim() || undefined,
            /**
             * ⚠️ LA FILIATION STRUCTURÉE. `pereNom`/`mereNom` ne sont PLUS
             * produits : un parent s'identifie. Le backend les lit encore pour
             * les dossiers déjà déposés, il ne les reçoit plus d'ici.
             */
            pere: parent(prefixe, 'Pere'),
            mere: parent(prefixe, 'Mere'),
            situationMatrimoniale: c('Situation') ?? 'celibataire',
            regimeMatrimonial: c('Situation') === 'marie' ? (c('Regime') ?? 'inconnu') : undefined,
            conjointNom: c('Situation') === 'marie' ? (c('ConjointNom')?.trim() || undefined) : undefined,
            /**
             * ⚠️ TROIS ÉTATS : `true` recueilli, `false` refusé, absent = non
             * demandé. Envoyer `false` par défaut ferait passer un oubli pour
             * un refus, et bloquerait des dépôts réguliers.
             */
            conjointConsent: c('Situation') === 'marie'
              ? (c('ConjointConsent') === true ? true : undefined) : undefined,
            pieceType: c('PieceType') ?? PIECE_PAR_DEFAUT,
            pieceNumero: c('PieceNumero')?.trim(),
            pieceDelivreeLe: c('PieceDelivreeLe') || undefined,
            nin: c('Nin')?.trim() || undefined,
            /**
             * ⚠️ LE FAIT, PAS L'IMAGE. Le portrait fait 40 à 60 Ko en base64 ;
             * quatre par acte dans une colonne `jsonb` conservée pour la vie du
             * registre alourdiraient chaque lecture d'un quart de mégaoctet.
             * L'image part au COFFRE, chiffrée ; ce booléen dit qu'elle existe,
             * ce qui permet à l'acte de distinguer « aucune photo n'a été
             * prise » de « la photo existe et je ne sais pas la retrouver ».
             */
            portraitPresent: c('Portrait') ? true : undefined,
            telephone: c('Telephone')?.trim(),
            courriel: c('Courriel')?.trim() || undefined,
            adresse,
          },
        };
  }
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  /* ═══ LE PARCOURS ════════════════════════════════════════════════════════
   * ⚠️ `etape` compte les CADRES ; l'étape `RECAP` est la dernière, et elle
   * n'est pas un cadre : c'est la relecture de ce qui va partir. */
  const [etape, setEtape] = useState(1);
  const [cadres, setCadres] = useState<Array<{ n: number; titre: string }>>([]);
  const inscrire = useCallback((n: number, titre: string) => {
    setCadres((x) => (x.some((c) => c.n === n && c.titre === titre)
      ? x : [...x.filter((c) => c.n !== n), { n, titre }].sort((a, b) => a.n - b.n)));
  }, []);
  const RECAP = cadres.length + 1;
  const formRef = useRef<HTMLFormElement>(null);

  /**
   * CE QUI MANQUE, PAR CADRE — mesuré sur le FORMULAIRE RENDU.
   *
   * ⚠️ ON COMPTE LES CHAMPS RÉELLEMENT AFFICHÉS, pas une liste écrite à côté.
   * Ce formulaire porte dix types d'acte, deux parties, deux témoins, un
   * défunt et jusqu'à trente héritiers : une table des champs obligatoires par
   * type divergerait du premier champ ajouté, et le désaccord se lirait
   * « l'étape est complète » sur un cadre qui ne l'est pas. Le DOM, lui, ne
   * peut pas mentir sur ce qu'il affiche.
   *
   * ⚠️ RECALCULÉ À CHAQUE FRAPPE — `onInput` sur le formulaire, pas un état
   * dérivé de `f` : les champs composés (adresse en cascade, NIN) écrivent
   * plusieurs clés, et certains cadres n'ont aucune clé dans `f`.
   */
  const [manques, setManques] = useState<Record<number, number>>({});
  const mesurer = useCallback(() => {
    const el = formRef.current;
    if (!el) return;
    const m: Record<number, number> = {};
    el.querySelectorAll<HTMLFieldSetElement>('fieldset.cadre[data-etape]').forEach((fs) => {
      const n = Number(fs.dataset.etape);
      const requis = fs.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[required]');
      m[n] = [...requis].filter((c) => !String(c.value ?? '').trim()).length;
    });
    setManques(m);
  }, []);
  useEffect(() => { mesurer(); });

  /* ═══ LE BROUILLON ═══════════════════════════════════════════════════════
   * ⚠️ IL VIT DANS LE NAVIGATEUR, ET C'EST UNE LIMITE ASSUMÉE. Le registre
   * n'a pas d'état « brouillon » : `fon.demande` naît à la validation, et son
   * commentaire dit pourquoi — « éviter d'ouvrir un dossier pour un formulaire
   * abandonné en cours de saisie ». Un brouillon local survit à un onglet
   * fermé et à un navigateur qui tombe ; il ne suit pas l'agent d'un poste à
   * l'autre. Item D.966 pour le brouillon de service.
   *
   * ⚠️ UN BROUILLON QUI NE S'ÉCRIT PAS DOIT LE DIRE. Silencieux, il fait
   * travailler une heure sur un formulaire qui n'existe nulle part. */
  const cleService = `${type.code}.${parcelleDesignee ?? 'sans-parcelle'}`;
  const cleBrouillon = `cadastral.brouillon.${cleService}`;
  const [brouillon, setBrouillon] = useState<
    { le: string | null; echec: boolean; ou: 'local' | 'service' | null }>(
    { le: null, echec: false, ou: null });
  const restaure = useRef(false);

  /**
   * ⚠️⚠️ DEUX BROUILLONS PEUVENT EXISTER, ET LE PLUS RÉCENT GAGNE — EN LE
   * DISANT. Le navigateur en garde un (instantané, il survit à une coupure),
   * le service en garde un (il suit l'agent d'un poste à l'autre). Un agent
   * qui change de poste a donc un local ANCIEN et un service RÉCENT ; prendre
   * le local sans regarder effacerait le travail fait ailleurs, et prendre le
   * service sans regarder effacerait ce qui vient d'être tapé hors ligne.
   *   On compare les horodatages, et on NOMME la source retenue : un
   *   remplissage silencieux fait croire à une perte, ou pire, la masque.
   */
  useEffect(() => {
    if (restaure.current) return;
    restaure.current = true;
    let local: { f?: Record<string, unknown>; etape?: number; le?: string } | null = null;
    try {
      const brut = window.localStorage.getItem(cleBrouillon);
      if (brut) local = JSON.parse(brut);
    } catch { /* stockage refusé ou brouillon illisible : on part d'un formulaire neuf */ }

    void (async () => {
      let service: { f: Record<string, unknown>; etape: number; le: string } | null = null;
      try {
        const r = await api.demandes.brouillons();
        const mien = r.brouillons.find((b) => b.cle === cleService);
        if (mien) {
          const plein = await api.demandes.brouillonCorps(cleService);
          if (plein.corps && Object.keys(plein.corps).length > 0) {
            service = { f: plein.corps, etape: mien.etape, le: mien.maj_le };
          }
        }
      } catch { /* service injoignable : le local suffit à travailler */ }

      const pris = (!service) ? (local?.f ? { ...local, ou: 'local' as const } : null)
        : (!local?.f || !local.le || new Date(service.le) > new Date(local.le))
          ? { f: service.f, etape: service.etape, le: service.le, ou: 'service' as const }
          : { ...local, ou: 'local' as const };
      if (!pris?.f || Object.keys(pris.f).length === 0) return;
      setF((x) => ({ ...x, ...pris.f }));
      if (pris.etape) setEtape(pris.etape);
      setBrouillon({ le: pris.le ?? null, echec: false, ou: pris.ou });
    })();
  }, [cleBrouillon, cleService]);

  /* Le local : instantané, et il tient hors ligne. */
  useEffect(() => {
    if (!restaure.current) return;
    const t = window.setTimeout(() => {
      try {
        const le = new Date().toISOString();
        window.localStorage.setItem(cleBrouillon, JSON.stringify({ f, etape, le }));
        setBrouillon((b) => ({ le, echec: false, ou: b.ou }));
      } catch { setBrouillon((b) => ({ le: null, echec: true, ou: b.ou })); }
    }, 500);
    return () => window.clearTimeout(t);
  }, [f, etape, cleBrouillon]);

  /**
   * Le service : plus lent, et c'est voulu.
   *
   * ⚠️ TROIS SECONDES, PAS CINQ CENTS MILLISECONDES. Le corps porte les
   * portraits en base64 — quatre photographies réduites pèsent plus de cent
   * kilo-octets. À la cadence du local, chaque frappe les renverrait sur une
   * liaison guinéenne. Le local reste la mémoire immédiate ; le service est la
   * mémoire qui voyage.
   *
   * ⚠️ ET UN ÉCHEC D'ENVOI N'EFFACE PAS « enregistré » : le local, lui, a
   * bien écrit. On distingue les deux à l'écran.
   */
  const [auService, setAuService] = useState<'jamais' | 'fait' | 'echec'>('jamais');
  useEffect(() => {
    if (!restaure.current) return;
    if (Object.keys(f).length === 0) return;
    const t = window.setTimeout(() => {
      void api.demandes.enregistrerBrouillon(cleService, {
        typeCode: type.code, corps: f, etape,
        parcelle: parcelleDesignee ?? null,
        /* Ce que l'agent lira dans « Mes saisies en cours » — pas un identifiant. */
        resume: [f.nom, f.prenoms].filter(Boolean).join(' ').trim().slice(0, 200) || null,
      }).then(() => setAuService('fait')).catch(() => setAuService('echec'));
    }, 3000);
    return () => window.clearTimeout(t);
  }, [f, etape, cleService, type.code, parcelleDesignee]);

  function construire() {
    const personne = qualite === 'personne_physique'
      ? {
          nom: f.nom?.trim(), prenoms: f.prenoms?.trim(),
          dateNaissance: f.dateNaissance, lieuNaissance: f.lieuNaissance?.trim(),
          nationalite: f.nationalite?.trim() || 'Guinéenne',
          profession: f.profession?.trim() || undefined,
          pereNom: f.pereNom?.trim() || undefined,
          mereNom: f.mereNom?.trim() || undefined,
          pieceType: f.pieceType, pieceNumero: f.pieceNumero?.trim(),
          pieceDelivreeLe: f.pieceDelivreeLe || undefined,
          nin: f.nin?.trim() || undefined,
          telephone: f.telephone?.trim(),
          courriel: f.courriel?.trim() || undefined,
          adresse: {
            region: f.region, prefecture: f.prefecture?.trim(),
            communeOuSousPrefecture: f.commune?.trim(),
            quartierOuDistrict: f.quartier?.trim() || undefined,
            precision: f.precision?.trim() || undefined,
          },
        }
      : {
          denomination: f.denomination?.trim(), formeJuridique: f.formeJuridique?.trim(),
          rccm: f.rccm?.trim() || undefined, nif: f.nif?.trim() || undefined,
          siegeAdresse: {
            region: f.region, prefecture: f.prefecture?.trim(),
            communeOuSousPrefecture: f.commune?.trim(),
            quartierOuDistrict: f.quartier?.trim() || undefined,
          },
          representantNom: f.repNom?.trim(), representantPrenoms: f.repPrenoms?.trim(),
          representantQualite: f.repQualite?.trim(),
          telephone: f.telephone?.trim(), courriel: f.courriel?.trim() || undefined,
        };

    const demandeur = {
      qualite, personne, agitPour,
      /**
       * ⚠️⚠️ LA CLÉ EST ABSENTE, PAS `undefined`. Écrire
       * `mandantNom: … : undefined` laisse la CLÉ dans l'objet : `res.json` la
       * supprime au transport, mais le récapitulatif lit l'objet VIVANT, avant
       * l'envoi — et il affichait « Mandant : à compléter » à un agent qui
       * dépose pour son propre compte. « Sans objet » et « à compléter »
       * n'appellent pas le même geste : c'est la règle déjà écrite pour le
       * régime matrimonial d'un célibataire et pour le téléphone d'un défunt.
       */
      ...(agitPour === 'tiers' ? {
        mandantNom: f.mandantNom?.trim(),
        mandatReference: f.mandatReference?.trim(),
      } : {}),
      /* ⚠️ Même règle : clé absente tant que la qualité n'est pas déclarée. */
      ...(estPartie ? { estPartie } : {}),
    };

    const immeuble = (f.immPrefecture || f.immLieuDit || f.immIdentifiant) ? {
      region: f.immRegion, prefecture: f.immPrefecture?.trim(),
      communeOuSousPrefecture: f.immCommune?.trim(),
      quartierOuDistrict: f.immQuartier?.trim() || undefined,
      identifiantParcelle: f.immIdentifiant?.trim() || undefined,
      referenceCadastrale: f.immReference?.trim() || undefined,
      lieuDit: f.immLieuDit?.trim() || undefined,
      lotCopropriete: f.immLot?.trim() || undefined,
    } : undefined;

    const personneInscrite = f.piNom?.trim() ? {
      nom: f.piNom.trim(), prenoms: f.piPrenoms?.trim() || undefined,
      dateNaissance: f.piNaissance || undefined,
      seulTitulaire: Boolean(f.piSeul), avecAutres: Boolean(f.piAutres),
    } : undefined;

    if (type.code === 'extrait_cadastral') {
      return { demandeur, immeuble, personneInscrite, objet: f.objet,
               motif: f.motif?.trim(), engagementConfidentialite: engagement };
    }
    if (type.code === 'immatriculation_fonciere') {
      return { demandeur, immeuble: immeuble ?? {}, origineDetention: f.origine ?? 'heritage',
               origineDetail: f.origineDetail?.trim(), detientDepuis: f.detientDepuis || undefined,
               riverains: [], piecesJointes: [], engagementConfidentialite: engagement };
    }
    if (type.code === 'donation') {
      return {
        demandeur, immeuble: immeuble ?? {},
        donateur: partie('don'), donataire: partie('dat'),
        origine: origine(),
        temoins: temoins(),
        lienEntreParties: f.lien ?? 'ascendant_descendant',
        natureDroitDonne: f.natureDroit ?? 'propriete',
        quotiteNum: Number(f.quotiteNum ?? 1), quotiteDen: Number(f.quotiteDen ?? 1),
        chargesEtConditions: f.charges?.trim() || undefined,
        acceptationDonataire: acceptation,
        declarationSansContrepartie: declaration,
        piecesJointes: [], engagementConfidentialite: engagement,
      };
    }
    /**
     * ⚠️⚠️ LA SUCCESSION — ET C'EST LE TROISIÈME TYPE QUE CE GUICHET APPREND À
     * RECEVOIR APRÈS COUP. Comme la vente et la mainlevée, `succession` était
     * APPLICABLE au registre et INDEMANDABLE ici. Le point qui compte : ce
     * formulaire ne recueille AUCUNE case d'acceptation, et son absence est une
     * règle, pas un oubli. Il n'y a pas deux volontés dans une succession — la
     * transmission s'opère par la mort. L'option successorale (accepter,
     * accepter à concurrence de l'actif net, renoncer) s'exerce PAR CHAQUE
     * HÉRITIER, après connaissance du passif, sur la fiche de succession.
     * La recueillir ici ferait accepter une dette dont personne ne connaît
     * encore le montant.
     */
    /**
     * ⚠️⚠️ LA VENTE ÉTAIT AU CATALOGUE DEPUIS LE 20/09 ET REMPLISSABLE NULLE
     * PART. Mesuré le 21/09 : `fon.demande_type` la porte, `FORMULAIRES` la
     * porte, `doc.acte_autorite` la porte — et cet écran levait « aucun corps
     * de demande n'est construit pour le type vente ». Le refus était JUSTE et
     * explicite (c'est le repli silencieux retiré plus bas qui aurait été
     * grave), mais il rendait l'acte le plus courant du registre indéposable
     * par un agent. Trois couches sur quatre au service, zéro à l'écran : la
     * mesure du service disait « chaîne complète ».
     *
     * ⚠️ LA SYMÉTRIE AVEC LA DONATION EST DÉLIBÉRÉE, ET SON ASYMÉTRIE AUSSI :
     * la donation déclare qu'il n'y a AUCUNE contrepartie, la vente déclare la
     * SINCÉRITÉ d'un prix strictement positif. Chacune ferme la porte que
     * l'autre ouvre — une vente à 0 est une donation, avec une autre assiette
     * de droits et une autre réserve héréditaire.
     */
    if (type.code === 'vente') {
      return {
        demandeur, immeuble: immeuble ?? {},
        vendeur: partie('ven'), acquereur: partie('acq'),
        origine: origine(),
        natureDroitVendu: f.natureDroit ?? 'propriete',
        quotiteNum: Number(f.quotiteNum ?? 1), quotiteDen: Number(f.quotiteDen ?? 1),
        /**
         * ⚠️ UN ENTIER DE FRANCS GUINÉENS, PAS UN FLOTTANT. Le GNF n'a pas de
         * subdivision en usage, et un flottant sur un prix d'immeuble perd des
         * unités au passage. `Number('')` vaut 0 — que le schéma REFUSE
         * (`.positive()`), et c'est voulu : une vente à zéro entrerait sous le
         * mauvais régime fiscal.
         */
        prix: f.prix === undefined || f.prix === '' ? undefined : Number(f.prix),
        devise: 'GNF',
        modalitesPaiement: f.modalites?.trim(),
        /**
         * ⚠️ « QUITTANCE » N'EST PAS « PRIX ». Convenir d'un prix n'est pas
         * l'avoir reçu. Le champ est obligatoire et booléen : décoché vaut
         * « non payé », qui est parfaitement licite (vente à terme) — et ce
         * n'est PAS la même chose qu'un silence.
         */
        quittancePrix: Boolean(f.quittance),
        entreeEnJouissance: f.jouissance?.trim() || undefined,
        garantieEviction: f.eviction !== false,
        temoins: temoins(),
        chargesEtConditions: f.charges?.trim() || undefined,
        declarationSincerite: declaration,
        piecesJointes: [], engagementConfidentialite: engagement,
      };
    }
    /**
     * ⚠️⚠️ LA MAINLEVÉE — MÊME HISTOIRE, UN JOUR PLUS TARD. Entrée au catalogue
     * le 21/09 avec ses cinq couches de service, et pas de sixième : l'écran ne
     * savait pas la construire. Le registre savait libérer un fonds et l'agent
     * ne pouvait pas le demander.
     *
     * ⚠️ LE CRÉANCIER EST LE PREMIER CADRE, PAS LE DÉBITEUR, et l'ordre EST la
     * règle : c'est celui qui se dessaisit qui ouvre l'acte. Le débiteur a le
     * plus grand intérêt à la mainlevée et AUCUN pouvoir de la donner.
     */
    if (type.code === 'mainlevee') {
      return {
        demandeur, immeuble: immeuble ?? {},
        creancier: partie('cre'), debiteur: partie('deb'),
        natureSurete: f.natureSurete ?? 'hypotheque',
        /**
         * ⚠️ LIBRE ET OBLIGATOIRE À LA FOIS. Le requérant connaît le numéro
         * figurant sur SON acte, pas l'identifiant interne du droit : exiger un
         * UUID viderait le guichet. Mais une mainlevée qui ne désigne pas ce
         * qu'elle lève oblige l'agent à deviner laquelle des quatre hypothèques
         * du fonds tombe.
         */
        referenceInscription: f.refInscription?.trim(),
        rangInscription: f.rangInscription ? Number(f.rangInscription) : undefined,
        montantGaranti: f.montantGaranti ? Number(f.montantGaranti) : undefined,
        devise: 'GNF',
        causeExtinction: f.causeExtinction || undefined,
        causeDetail: f.causeDetail?.trim() || undefined,
        quittanceIntegrale: Boolean(f.quittance),
        /**
         * ⚠️ SANS CE CONSENTEMENT, IL N'Y A PAS DE MAINLEVÉE — il y a une
         * demande adressée au créancier. Le registre ne peut pas vérifier
         * qu'une banque a consenti ; il peut exiger que quelqu'un le DÉCLARE.
         */
        consentementRadiation: acceptation,
        temoins: temoins(),
        piecesJointes: [], engagementConfidentialite: engagement,
      };
    }
    /**
     * L'INSCRIPTION DE SÛRETÉ — le miroir entrant de la mainlevée.
     *
     * ⚠️⚠️ CE TYPE COMPTAIT 4 MUTATIONS EN BASE ET AUCUNE PORTE. Le guichet
     * savait LIBÉRER un fonds et pas le GREVER : la moitié sortante d'un
     * mécanisme, sans sa moitié entrante. *Livrer une moitié est plus
     * trompeur que n'en livrer aucune : on conclut que le mécanisme existe.*
     *
     * ⚠️ LE CONSTITUANT EST LE PREMIER CADRE, ET C'EST L'INVERSE DE LA
     * MAINLEVÉE. Celui qui se dessaisit ouvre l'acte : à la mainlevée c'est le
     * créancier qui abandonne sa sûreté ; ici c'est le propriétaire qui grève
     * son fonds. Recopier l'ordre de la mainlevée aurait mis en tête celui qui
     * ne donne rien.
     */
    if (type.code === 'inscription_surete') {
      return {
        demandeur, immeuble: immeuble ?? {},
        constituant: partie('cst'), creancier: partie('cre'),
        natureSurete: f.natureSurete ?? 'hypotheque',
        /**
         * ⚠️ PAS DE `?? 1` SUR LE RANG. Un rang par défaut ferait porter au
         * registre que le créancier vient EN PREMIER alors que personne ne l'a
         * dit — et le rang décide qui est payé le jour de la vente forcée.
         * Le champ est obligatoire à l'écran ; ici, une absence reste absente
         * et le service la refuse en le disant.
         */
        rangDemande: f.rangDemande ? Number(f.rangDemande) : undefined,
        montantGaranti: f.montantGaranti ? Number(f.montantGaranti) : undefined,
        devise: 'GNF',
        causeCreance: f.causeCreance?.trim(),
        titreConstitutif: f.titreConstitutif?.trim(),
        dateTitreConstitutif: f.dateTitreConstitutif || undefined,
        /**
         * ⚠️ SANS CE CONSENTEMENT, IL N'Y A PAS D'INSCRIPTION — il y a une
         * demande adressée au propriétaire du fonds. Miroir exact du
         * consentement du créancier sur la mainlevée.
         */
        consentementConstituant: acceptation,
        temoins: temoins(),
        piecesJointes: [], engagementConfidentialite: engagement,
      };
    }

    /**
     * L'ÉCHANGE — un contrat, deux fonds.
     *
     * ⚠️⚠️ LE SECOND IMMEUBLE EST CONSTRUIT À PART, et c'est le seul endroit du
     * guichet où deux biens coexistent. `immeuble` (le cadre commun, préfixe
     * `imm`) porte le fonds du PREMIER copermutant ; le second a ses propres
     * champs (préfixe `im2`). Réutiliser le cadre commun pour les deux aurait
     * écrasé l'un par l'autre — et l'écran l'aurait fait en silence.
     *
     * ⚠️ AUCUN REPLI SUR `{}` POUR LE SECOND. Un échange sans second fonds
     * n'est pas un échange ; laisser passer un objet vide ferait déposer une
     * demande que le service refuse ensuite pour un champ que l'agent croyait
     * facultatif.
     */
    if (type.code === 'echange') {
      return {
        demandeur,
        premierCopermutant: partie('cp1'), secondCopermutant: partie('cp2'),
        immeuble: immeuble ?? {},
        immeubleSecond: {
          region: f.im2Region, prefecture: f.im2Prefecture?.trim(),
          communeOuSousPrefecture: f.im2Commune?.trim(),
          quartierOuDistrict: f.im2Quartier?.trim() || undefined,
          identifiantParcelle: f.im2Identifiant?.trim() || undefined,
          referenceCadastrale: f.im2Reference?.trim() || undefined,
          lieuDit: f.im2LieuDit?.trim() || undefined,
        },
        valeurPremierLot: f.valeurLot1 ? Number(f.valeurLot1) : undefined,
        valeurSecondLot: f.valeurLot2 ? Number(f.valeurLot2) : undefined,
        /* ⚠️ Une soulte à zéro n'est PAS une absence de soulte : `Number('')`
           rend 0, et 0 passerait pour un montant déclaré. On n'envoie que ce
           qui a été saisi. */
        soulteMontant: f.soulteMontant ? Number(f.soulteMontant) : undefined,
        soulteDebiteur: f.soulteDebiteur || undefined,
        devise: 'GNF',
        lotsDeclaresEgaux: Boolean(f.lotsEgaux),
        dateActeEchange: f.dateActeEchange || undefined,
        temoins: temoins(),
        piecesJointes: [], engagementConfidentialite: engagement,
      };
    }

    /**
     * LA RADIATION — elle CONSTATE, elle ne se consent pas.
     *
     * ⚠️⚠️ AUCUNE CASE DE CONSENTEMENT ICI, ET C'EST LE POINT. La mainlevée
     * ÉMANE du créancier ; la radiation est OPÉRÉE par le conservateur sur une
     * cause extérieure — jugement, péremption, mainlevée déjà donnée. Demander
     * « consentez-vous ? » sur une radiation fondée sur un jugement ferait
     * croire qu'un créancier peut s'y opposer. Ce qui remplace le consentement,
     * c'est la RÉFÉRENCE de l'acte ou de la décision qui la fonde.
     */
    if (type.code === 'radiation') {
      return {
        demandeur, immeuble: immeuble ?? {},
        titulaireInscription: partie('tit'), proprietaireFonds: partie('prp'),
        natureSurete: f.natureSurete || undefined,
        referenceInscription: f.refInscription?.trim(),
        rangInscription: f.rangInscription ? Number(f.rangInscription) : undefined,
        causeRadiation: f.causeRadiation || undefined,
        causeDetail: f.causeDetail?.trim() || undefined,
        referenceCause: f.referenceCause?.trim(),
        dateCause: f.dateCause || undefined,
        /**
         * ⚠️ `Boolean(f.radiationTotale)` ET NON UN DÉFAUT À `true`. Une
         * radiation annoncée totale alors que nul ne l'a dite laisse croire au
         * lecteur que le fonds est libre — et il prête dessus.
         */
        radiationTotale: Boolean(f.radiationTotale),
        montantRadie: f.montantRadie ? Number(f.montantRadie) : undefined,
        piecesJointes: [], engagementConfidentialite: engagement,
      };
    }

    if (type.code === 'succession') {
      return {
        demandeur,
        defunt: { qualite: 'personne_physique', personne: defunt() },
        dateDeces: f.dcDateDeces || undefined,
        lieuDeces: f.dcLieuDeces?.trim(),
        /**
         * ⚠️ AUCUN REPLI SUR UNE VALEUR PAR DÉFAUT ICI. `f.sourceDeces ?? 'acte_deces'`
         * ferait porter au registre qu'un acte de décès existe quand l'agent
         * n'a rien choisi — et c'est sur cette mention qu'un tiers s'appuiera
         * dans dix ans. `undefined` fait rendre au service un refus qui NOMME
         * le champ ; un défaut silencieux ne fait rien rendre du tout.
         */
        sourceDeces: f.sourceDeces || undefined,
        referenceActeDeces: f.refActeDeces?.trim() || undefined,
        sourceDevolution: f.sourceDevolution || undefined,
        referenceDevolution: f.refDevolution?.trim() || undefined,
        heritiers: heritiers(),
        immeuble: immeuble ?? {},
        piecesJointes: [], engagementConfidentialite: engagement,
      };
    }
    if (type.code === 'duplicata_titre') {
      return { demandeur, identifiantTitre: f.identifiantTitre?.trim(), cause: f.cause ?? 'perte',
               circonstances: f.circonstances?.trim(),
               declarationSurHonneur: declaration, engagementConfidentialite: engagement };
    }
    /**
     * ⚠️ LE REPLI SILENCIEUX A ÉTÉ RETIRÉ ICI, ET IL AVAIT DÉJÀ MORDU. Cette
     * fonction finissait par `return { …duplicata… }` SANS condition : tout
     * type nouveau au catalogue — la donation en est la preuve — envoyait le
     * corps d'un duplicata de titre. L'usager remplissait un formulaire de
     * donation, le service recevait `identifiantTitre: undefined`, et le dépôt
     * rendait 400 sur un champ que l'écran n'affichait même pas. Un repli
     * « je ne connais pas ce type, j'envoie le dernier » est indiscernable
     * d'un formulaire juste, jusqu'au clic.
     */
    throw new Error(
      `Aucun corps de demande n'est construit pour le type « ${type.code} ». `
      + `L'écran connaît : extrait_cadastral, immatriculation_fonciere, donation, vente, mainlevee, succession, duplicata_titre, inscription_surete, radiation, echange. `
      + `Ce type est au catalogue du service et pas ici — c'est un manque de cet écran, pas une erreur de saisie.`);
  }

  async function soumettre(e: React.FormEvent) {
    e.preventDefault();
    setErreur(null); setEnvoi(true);
    try {
      const r = await api.demandes.deposer({
        type: type.code, formulaire: construire(),
        identifiantParcelle: f.immIdentifiant?.trim() || undefined,
        /* ⚠️ Le SERVEUR jette la saisie, dans la transaction du dépôt. Un
           appel de suppression séparé peut échouer là où le dépôt a réussi :
           l'agent retrouverait son brouillon et déposerait deux fois. */
        cleBrouillon: cleService,
      });

      /**
       * ⚠️⚠️ ET LA COPIE LOCALE SE JETTE AUSSI — elle ne l'était JAMAIS.
       * Défaut mesuré le 24/09/2026 en câblant le brouillon de service : rien
       * n'appelait `removeItem` après un dépôt réussi. Sur un type à clé
       * stable — un duplicata, qui n'a pas de parcelle — le formulaire suivant
       * ressuscitait la saisie d'une demande DÉJÀ DÉPOSÉE, champs remplis,
       * prête à repartir. L'agent n'avait aucun moyen de voir que ce qu'il
       * relisait avait déjà été enregistré.
       */
      try { window.localStorage.removeItem(cleBrouillon); } catch { /* stockage refusé */ }

      /**
       * ═══════════════════════════════════════════════════════════════════
       * ⚠️⚠️ LES PORTRAITS PARTENT APRÈS, ET L'ORDRE EST CONTRAINT.
       * ═══════════════════════════════════════════════════════════════════
       * Une photographie est un DOCUMENT : elle va au coffre, chiffrée. Or
       * `POST /documents` exige au moins un rattachement, et le seul objet qui
       * convienne — l'AFFAIRE — n'existe qu'une fois la demande déposée. Les
       * envoyer avant produirait un objet chiffré rattaché à rien : c'est
       * exactement ce qui a fait **6 rattachements sur 137** dans ce registre.
       *
       * ⚠️ UN ÉCHEC DE PORTRAIT N'ANNULE PAS LE DÉPÔT, et il ne se tait pas.
       * La demande est enregistrée, l'affaire ouverte, le récépissé dû : les
       * défaire pour une photo serait pire. Mais l'agent doit SAVOIR, parce que
       * l'acte ne se délivrera pas sans elle — `fon.portrait_de_partie` rendra
       * « aucun portrait » pour cette partie, et le message le dit ici.
       */
      const portraits: Array<[string, string, string]> = [
        ...(['don', 'ven', 'cst', 'cp1', 'tit'] as const)
          .filter((x) => f[`${x}Portrait`])
          .map((x) => ['portrait_cedant', f[`${x}Portrait`] as string, QUI_EST_CE[x] ?? x] as [string, string, string]),
        ...(['dat', 'acq', 'cre', 'deb', 'cp2', 'prp'] as const)
          .filter((x) => f[`${x}Portrait`])
          .map((x) => ['portrait_beneficiaire', f[`${x}Portrait`] as string, QUI_EST_CE[x] ?? x] as [string, string, string]),
        ...(['t1', 't2'] as const)
          .filter((x) => f[`${x}Portrait`])
          .map((x, i) => ['portrait_temoin', f[`${x}Portrait`] as string, `témoin n° ${i + 1}`] as [string, string, string]),
      ];
      const echecs: string[] = [];
      for (const [codePiece, donnees, qui] of portraits) {
        try {
          await api.affaires.televerserPiece(r.affaire, {
            codePiece, typeCode: codePiece,
            emetteur: `Guichet — ${qui}`,
            dateDocument: new Date().toISOString().slice(0, 10),
            /* ⚠️ Le type MIME vient de l'en-tête de la donnée, pas d'une
               supposition : `doc.garde_portrait` refuse tout ce qui n'est pas
               une image, et le refus doit porter sur le vrai type. */
            typeMime: donnees.slice(5, donnees.indexOf(';')),
            contenuBase64: donnees.slice(donnees.indexOf(',') + 1),
            sensibilite: 2,
            motif: `Photographie de ${qui}, prise au guichet`,
          });
        } catch (e) {
          echecs.push(`${qui} (${e instanceof ErreurApi ? e.message : String(e)})`);
        }
      }
      if (echecs.length > 0) {
        setErreur(`La demande ${r.demande} est déposée, mais ${echecs.length} photographie(s) `
          + `n'ont pas été versées : ${echecs.join(' · ')}. `
          + `L'acte ne se délivrera pas sans elles — versez-les depuis l'affaire ${r.affaire}.`);
        return;
      }
      onDepose(r.demande);
    } catch (err) {
      /* ⚠️ Zod rend le CHEMIN du champ fautif ; on le montre tel quel. Un
         « formulaire invalide » sans dire lequel oblige à tout relire. */
      setErreur(err instanceof ErreurApi
        ? `${err.message}${err.details ? ` — ${JSON.stringify(err.details)}` : ''}`
        : String(err));
    } finally { setEnvoi(false); }
  }

  /** Le cadre qui porte ce bloc — ou `null`, et alors on n'offre pas le geste. */
  const cadreDuBloc = (cle: string) => {
    const t = BLOC_VERS_TITRE_DE_CADRE[cle];
    return t ? cadres.find((x) => x.titre.toLowerCase().includes(t)) ?? null : null;
  };

  const totalManques = Object.values(manques).reduce((n, x) => n + x, 0);
  const etatCadre = (n: number) =>
    (manques[n] ?? 0) === 0 ? 'fait' : 'partiel';
  const faits = cadres.filter((c) => etatCadre(c.n) === 'fait').length;

  return (
    <form className="formulaire-etat" ref={formRef} onSubmit={soumettre}
          /**
           * ⚠️⚠️ `noValidate` N'EST PAS UN RENONCEMENT À LA VALIDATION, C'EST
           * SA CONDITION. Les cadres non courants restent MONTÉS et cachés —
           * les démonter perdrait la saisie à chaque changement d'étape. Or le
           * navigateur refuse de soumettre un formulaire dont un champ
           * `required` est caché, avec « An invalid form control is not
           * focusable » : un refus muet, sans champ à montrer, sur un
           * formulaire que l'agent voit complet. Le contrôle passe donc par
           * `manques`, qui compte les mêmes `[required]` et sait, lui, DIRE
           * dans quel cadre ils sont.
           */
          noValidate
          onInput={mesurer} onChange={mesurer}>
    <ParcoursCtx.Provider value={{ etape, inscrire }}>
      {/* ═══════════════════════════════════════════════════════════════════
          ⚠️⚠️ CE QUE LE REGISTRE SAIT, ET CE QUI PÈSE — §2.3
          ═══════════════════════════════════════════════════════════════════
          « voir, AVANT d'ouvrir, ce qui pèse déjà sur ce fonds ».
          ⚠️ `affaireVivante` est le plus important : la garde
          `affaire_une_seule_vivante_par_parcelle` REFUSERA l'ouverture. Le dire
          ici épargne un formulaire entier rempli pour rien — c'est la même
          règle que « un geste fermé porte son motif », appliquée en amont. */}
      {preErreur && (
        <p className="avertissement" role="status">
          {preErreur}
        </p>
      )}
      {preRemplissage && (
        <section className={preRemplissage.cePesantDeja.affaireVivante ? 'avertissement' : 'fe-prerempli'}>
          <strong>
            Parcelle <code>{preRemplissage.parcelle.identifiant}</code> désignée sur la carte
          </strong>
          <p className="aide">
            {[preRemplissage.immeuble.quartierOuDistrict, preRemplissage.immeuble.communeOuSousPrefecture,
              preRemplissage.immeuble.prefecture, preRemplissage.immeuble.region]
              .filter(Boolean).join(' · ') || 'localisation inconnue du registre'}
            {preRemplissage.parcelle.surfaceM2 != null
              && ` — ${Math.round(preRemplissage.parcelle.surfaceM2).toLocaleString('fr-FR')} m²`}
            {' '}· l'immeuble est pré-rempli : corrigez-le si le terrain dit autre chose.
          </p>

          {/* ⚠️⚠️ TOUTES LES RÉFÉRENCES, PAS SEULEMENT CELLES QUE LE FORMULAIRE
              REÇOIT — demandé par le fondateur le 23/09/2026. Le schéma n'a
              qu'un champ `referenceCadastrale` ; l'agent, lui, doit LIRE la
              section et la circonscription pour vérifier qu'il est bien sur le
              fonds qu'on lui décrit. Les taire parce qu'aucun champ ne les
              reçoit reviendrait à cacher ce que le registre sait. */}
          <dl className="fe-refs">
            <dt>Section</dt><dd><code>{preRemplissage.cadastre.section ?? '—'}</code></dd>
            <dt>N° dans la section</dt>
            <dd><code>{preRemplissage.cadastre.numeroDansLaSection ?? '—'}</code></dd>
            <dt>Circonscription</dt>
            <dd>{preRemplissage.cadastre.circonscription
              ? `${preRemplissage.cadastre.circonscription}${preRemplissage.cadastre.circonscriptionCode
                  ? ` (${preRemplissage.cadastre.circonscriptionCode})` : ''}`
              : '—'}</dd>
            <dt>Division</dt>
            <dd>{preRemplissage.cadastre.division
              ? `${preRemplissage.cadastre.division}${preRemplissage.cadastre.divisionCode
                  ? ` · ${preRemplissage.cadastre.divisionCode}` : ''}`
              : '—'}</dd>
            {/* Affichée même absente : son absence dit qu'aucun antécédent
                papier n'est inscrit — un champ retiré se lirait « oubli ». */}
            <dt>Réf. héritée</dt>
            <dd>{preRemplissage.cadastre.referenceHeritee ?? <span className="aide">aucune inscrite</span>}</dd>
          </dl>

          {preRemplissage.titulairesInscrits.length > 0 && (
            <p className="aide">
              Titulaire{preRemplissage.titulairesInscrits.length > 1 ? 's' : ''} inscrit
              {preRemplissage.titulairesInscrits.length > 1 ? 's' : ''} :{' '}
              {preRemplissage.titulairesInscrits
                .map((t) => `${t.denomination} (${t.quotite})`).join(', ')}
              {/* ⚠️ PROPOSÉ, JAMAIS POSÉ. Le registre connaît qui détient ;
                  il ne sait pas qui se présente au guichet. */}
              {' '}— à recopier dans le cadre du cédant si c'est bien lui qui se présente.
            </p>
          )}

          {preRemplissage.cePesantDeja.affaireVivante ? (
            <p>
              <strong>Une affaire vivante porte déjà ce fonds :{' '}
              <code>{preRemplissage.cePesantDeja.affaireVivante}</code>.</strong>{' '}
              Le registre n'en accepte qu'une à la fois : ce dépôt sera refusé tant
              qu'elle n'est pas close.
            </p>
          ) : (
            <p className="aide">
              Aucune affaire vivante sur ce fonds
              {preRemplissage.cePesantDeja.titres > 0
                && ` · ${preRemplissage.cePesantDeja.titres} titre(s) inscrit(s)`}
              {preRemplissage.cePesantDeja.restrictions > 0
                && ` · ${preRemplissage.cePesantDeja.restrictions} restriction(s)`}
              {preRemplissage.cePesantDeja.mutationsEnInstance > 0
                && ` · ${preRemplissage.cePesantDeja.mutationsEnInstance} mutation(s) en instance`}
              .
            </p>
          )}
        </section>
      )}

      <header className="fe-tete">
        <div>
          <div className="fe-etat">République de Guinée · Mowibo Cadastral</div>
          <h3>{type.libelle}</h3>
          <p className="fe-objet">{type.objet}</p>
        </div>
        <div className="fe-circuit">
          <span className="fe-circuit-titre">Circuit</span>
          {type.circuit.map((s, i) => (
            <span key={s} className="fe-etape">
              {i > 0 && <Icone nom="chevron" taille={10} />}{service(s)}
            </span>
          ))}
          {type.delai_jours && <span className="jeton">délai {type.delai_jours} j</span>}
        </div>
      </header>

      {/* ⚠️ L'ÉTAT DU BROUILLON EST DIT, MÊME QUAND IL VA BIEN. Un
          enregistrement silencieux ne rassure personne ; un échec silencieux
          fait travailler une heure pour rien. */}
      {/* ⚠️ DEUX MÉMOIRES, DEUX ÉTATS, ET ON NE LES CONFOND PAS. Le navigateur
          garde la saisie instantanément ; le service la garde pour qu'elle
          suive l'agent. Un seul voyant pour les deux laisserait croire que la
          saisie voyage alors qu'elle dort dans un onglet. */}
      <div className={`brouillon-etat${brouillon.echec ? ' echoue' : ''}`}>
        <i className="brouillon-pastille" aria-hidden="true" />
        <span>
          {brouillon.echec
            ? "Brouillon NON enregistré — le navigateur refuse le stockage local."
            : brouillon.le
              ? `Brouillon enregistré · ${new Date(brouillon.le).toLocaleTimeString('fr-FR')}`
              : 'Brouillon vierge'}
        </span>
        <span className={`brouillon-service ${auService}`}>
          {auService === 'fait'
            ? 'Au service — cette saisie vous suivra d’un poste à l’autre et figure dans « Mes saisies en cours ».'
            : auService === 'echec'
              ? 'PAS au service — elle ne vit que dans ce navigateur. Ne fermez pas ce poste sans déposer.'
              : 'Pas encore envoyée au service.'}
        </span>
        {/* ⚠️ ON NOMME LA SOURCE REPRISE. Un formulaire qui se remplit tout
            seul sans dire d'où fait chercher une perte — ou pire, la masque. */}
        {brouillon.ou === 'service' && (
          <span className="brouillon-repris">
            Repris de la saisie du service : elle était plus récente que celle de ce navigateur.
          </span>
        )}
      </div>

      <div className="demarche">
        <nav className="demarche-rail" aria-label="Étapes de la démarche">
          <div className="demarche-titre">
            La démarche — {cadres.length} cadre(s) + récapitulatif
          </div>
          {cadres.map((c) => {
            const etat = etatCadre(c.n);
            const reste = manques[c.n] ?? 0;
            return (
              <button key={c.n} type="button"
                      className={`demarche-etape ${etat}${etape === c.n ? ' courante' : ''}`}
                      aria-current={etape === c.n ? 'step' : undefined}
                      onClick={() => setEtape(c.n)}>
                <span className="demarche-jeton">{etat === 'fait' ? '✓' : c.n}</span>
                <span className="demarche-nom">{c.titre}
                  <span className="demarche-etat">
                    {reste === 0 ? 'complet'
                      : reste === 1 ? '1 champ à remplir' : `${reste} champs à remplir`}
                  </span>
                </span>
              </button>
            );
          })}
          {/* ⚠️ LE RÉCAPITULATIF EST UNE ÉTAPE, pas un bouton au pied. C'est la
              dernière chose que l'agent lit avant d'engager l'État, et la
              première que le certificateur lira après. */}
          <button type="button"
                  className={`demarche-etape ${totalManques === 0 ? 'fait' : ''}${etape === RECAP ? ' courante' : ''}`}
                  aria-current={etape === RECAP ? 'step' : undefined}
                  onClick={() => setEtape(RECAP)}>
            <span className="demarche-jeton">{totalManques === 0 ? '✓' : RECAP}</span>
            <span className="demarche-nom">Récapitulatif avant dépôt
              <span className="demarche-etat">
                {totalManques === 0 ? 'prêt à déposer'
                  : `${totalManques} champ(s) à remplir en tout`}
              </span>
            </span>
          </button>
          <div className="demarche-barre">
            <i style={{ width: `${cadres.length ? Math.round((faits / cadres.length) * 100) : 0}%` }} />
          </div>
          <p className="demarche-legende">
            {totalManques === 0
              ? 'Tout ce qui est obligatoire est renseigné.'
              : `${faits} cadre(s) complet(s) sur ${cadres.length}. On peut passer à l’étape suivante sans l’avoir finie : le brouillon garde tout.`}
          </p>
        </nav>

        <div className="demarche-corps">

      <Cadre n={1} titre="Le demandeur"
             aide="Si le demandeur agit pour un tiers, le mandat est obligatoire.">
        <div className="ligne-champs">
          <Champ label="Qualité" obligatoire>
            <select value={qualite} onChange={(e) => setQualite(e.target.value as any)}>
              <option value="personne_physique">Personne physique</option>
              <option value="personne_morale">Personne morale</option>
            </select>
          </Champ>
          {/* ⚠️⚠️ TROIS RÉPONSES, PAS DEUX — pour les six types qui ont deux
              parties. « Pour son propre compte » ne disait pas LAQUELLE des
              deux, et rien n'obligeait le déposant à être l'une d'elles : on
              pouvait déposer une donation sans être ni donateur, ni donataire,
              ni mandataire. La réponse commande aussi la recopie de l'état
              civil : vingt champs qui étaient retapés à l'identique trois
              cadres plus bas, et qui divergeaient d'un accent. */}
          {parties ? (
            /**
             * ⚠️⚠️ TROIS CARTES, PLUS UN MENU DÉROULANT. Demande du fondateur,
             * 24/09/2026, maquette à l'appui. Ce n'est pas une préférence
             * d'apparence : chacune des trois réponses a une CONSÉQUENCE
             * différente sur la suite du formulaire — deux cadres pré-remplis,
             * ou un mandat rendu obligatoire. Un menu déroulant cache les
             * options tant qu'on ne l'ouvre pas, et ne dit JAMAIS ce que le
             * choix entraîne : l'agent choisissait à l'aveugle, puis
             * découvrait un cadre « Mandat » surgi de nulle part.
             *
             * ⚠️ LA CONSÉQUENCE ÉCRITE EST VRAIE, PAS DÉCORATIVE. « son état
             * civil remplit le cadre » décrit `MIROIR_DEMANDEUR` ; « sans lui
             * la demande est irrecevable » décrit le `refine` de
             * `demandeurSchema`, qui REFUSE le dépôt sans mandat.
             */
            <fieldset className="choix-qualite">
              <legend>Le demandeur est</legend>
              <div className="choix-cartes">
                {([
                  ['cedant', parties.cedant[1],
                   `son état civil remplit le cadre « ${parties.cedant[1]} » : une seule saisie, une seule personne au registre`],
                  ['beneficiaire', parties.beneficiaire[1],
                   `son état civil remplit le cadre « ${parties.beneficiaire[1]} » : une seule saisie, une seule personne au registre`],
                  ['tiers', 'un tiers mandaté',
                   "le mandat devient obligatoire : sans le nom du mandant ET la référence du mandat, le dépôt est refusé"],
                ] as const).map(([v, titre, suite]) => {
                  const actif = v === 'tiers' ? agitPour === 'tiers' : estPartie === v && agitPour !== 'tiers';
                  return (
                    <label key={v} className={`choix-carte${actif ? ' actif' : ''}`}>
                      <input type="radio" name="qualite-demandeur" value={v} checked={actif}
                             onChange={() => {
                               setDelie(false);
                               if (v === 'tiers') { setEstPartie(null); setAgitPour('tiers'); }
                               else { setEstPartie(v); setAgitPour('soi_meme'); }
                             }} />
                      <span className="choix-corps">
                        <strong>{titre}</strong>
                        <span className="mention">{suite}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
              {/* ⚠️ « PAS ENCORE RÉPONDU » N'EST PAS « TIERS ». Sans cette
                  ligne, un cadre 1 laissé tel quel se lirait « le demandeur
                  agit pour son propre compte » alors qu'il n'a rien déclaré —
                  et le dépôt passerait sans que personne soit partie. */}
              {!estPartie && agitPour !== 'tiers' && (
                <p className="cadre-aide">
                  <b className="requis">*</b> Réponse attendue : le dépôt est refusé tant
                  que le demandeur n'est ni l'une des deux parties, ni mandataire.
                </p>
              )}
            </fieldset>
          ) : (
            <Champ label="Agit" obligatoire>
              <select value={agitPour} onChange={(e) => setAgitPour(e.target.value as any)}>
                <option value="soi_meme">Pour son propre compte</option>
                <option value="tiers">Pour le compte d'un tiers</option>
              </select>
            </Champ>
          )}
        </div>

        {qualite === 'personne_physique' ? (
          <>
            <div className="ligne-champs">
              <Champ label="Nom" obligatoire>
                <SaisieMajuscule valeur={f.nom ?? ''} onValeur={(v) => pose('nom', v)} required />
              </Champ>
              <Champ label="Prénoms" obligatoire aide="tous les prénoms de l'état civil">
                <SaisieMajuscule valeur={f.prenoms ?? ''} onValeur={(v) => pose('prenoms', v)} required />
              </Champ>
            </div>

            {/* ⚠️⚠️ LE NIN PASSE DE FACULTATIF À OBLIGATOIRE — décision du
                fondateur, 24/09/2026. Ce qui change surtout : il est VÉRIFIÉ
                ici. La clé 7-3-1 et le décodage (sexe, date de naissance)
                vivent en base depuis la migration 018 ; cet écran offrait un
                `<input>` nu, et le schéma acceptait six caractères. Un numéro
                mal formé partait donc au registre et n'explosait qu'à
                l'inscription du titulaire, en SQL brut, l'usager reparti. */}
            <Champ label="Identifiant national (NIN)" obligatoire
                   aide="15 chiffres — il porte déjà le sexe et la date de naissance, et c'est lui qui les vérifie">
              <ChampNin valeur={f.nin ?? ''} onValeur={(v) => pose('nin', v)}
                        dateNaissance={f.dateNaissance}
                        onDateDeduite={(iso) => pose('dateNaissance', iso)}
                        lieuNaissance={f.lieuNaissance}
                        onLieuDeduit={(nom) => pose('lieuNaissance', nom)} />
            </Champ>

            <div className="ligne-champs">
              <Champ label="Date de naissance" obligatoire>
                <input type="date" value={f.dateNaissance ?? ''} onChange={set('dateNaissance')} required />
              </Champ>
              <Champ label="Lieu de naissance" obligatoire>
                <SaisieMajuscule valeur={f.lieuNaissance ?? ''} onValeur={(v) => pose('lieuNaissance', v)} required />
              </Champ>
              <Champ label="Nationalité" obligatoire>
                <ChampNationalite valeur={f.nationalite ?? ''} onValeur={(v) => pose('nationalite', v)} requis />
              </Champ>
            </div>
            <div className="ligne-champs">
              <Champ label="Profession">
                <SaisieMajuscule valeur={f.profession ?? ''} onValeur={(v) => pose('profession', v)} />
              </Champ>
              <Champ label="Nom du père" aide="filiation, pour lever les homonymies">
                <SaisieMajuscule valeur={f.pereNom ?? ''} onValeur={(v) => pose('pereNom', v)} />
              </Champ>
              <Champ label="Nom de la mère">
                <SaisieMajuscule valeur={f.mereNom ?? ''} onValeur={(v) => pose('mereNom', v)} />
              </Champ>
            </div>
            <div className="ligne-champs">
              <Champ label="Pièce d'identité" obligatoire>
                <select value={f.pieceType} onChange={set('pieceType')}>
                  {PIECES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </Champ>
              <Champ label="Numéro de la pièce" obligatoire>
                <SaisieMajuscule valeur={f.pieceNumero ?? ''} onValeur={(v) => pose('pieceNumero', v)} required />
              </Champ>
              <Champ label="Délivrée le"><input type="date" value={f.pieceDelivreeLe ?? ''} onChange={set('pieceDelivreeLe')} /></Champ>
            </div>
          </>
        ) : (
          <>
            <div className="ligne-champs">
              <Champ label="Dénomination sociale" obligatoire>
                <input value={f.denomination ?? ''} onChange={set('denomination')} required />
              </Champ>
              <Champ label="Forme juridique" obligatoire>
                <input value={f.formeJuridique ?? ''} onChange={set('formeJuridique')} placeholder="SARL, SA, GIE…" required />
              </Champ>
            </div>
            <div className="ligne-champs">
              <Champ label="RCCM"><input value={f.rccm ?? ''} onChange={set('rccm')} /></Champ>
              <Champ label="NIF"><input value={f.nif ?? ''} onChange={set('nif')} /></Champ>
            </div>
            <div className="ligne-champs">
              <Champ label="Représentant — nom" obligatoire><input value={f.repNom ?? ''} onChange={set('repNom')} required /></Champ>
              <Champ label="Prénoms" obligatoire><input value={f.repPrenoms ?? ''} onChange={set('repPrenoms')} required /></Champ>
              <Champ label="Qualité" obligatoire><input value={f.repQualite ?? ''} onChange={set('repQualite')} placeholder="Gérant, PDG…" required /></Champ>
            </div>
          </>
        )}

        <div className="ligne-champs">
          <ChampsAdresse requis adresse={adresseDe(f, '')}
                         onAdresse={(a) => {
                           pose('region', a.region); pose('prefecture', a.prefecture);
                           pose('commune', a.commune); pose('quartier', a.quartier ?? '');
                         }} />
        </div>
        <div className="ligne-champs">
          <Champ label="Téléphone" obligatoire><input value={f.telephone ?? ''} onChange={set('telephone')} placeholder="+224…" required /></Champ>
          <Champ label="Courriel"><input type="email" value={f.courriel ?? ''} onChange={set('courriel')} /></Champ>
        </div>

        {agitPour === 'tiers' && (
          <div className="ligne-champs encadre-mandat">
            <Champ label="Mandant — nom" obligatoire aide="la personne pour qui vous agissez">
              <input value={f.mandantNom ?? ''} onChange={set('mandantNom')} required />
            </Champ>
            <Champ label="Référence du mandat" obligatoire aide="sans mandat, la demande est irrecevable">
              <input value={f.mandatReference ?? ''} onChange={set('mandatReference')} required />
            </Champ>
          </div>
        )}
      </Cadre>

      {type.code !== 'duplicata_titre' && (
        <Cadre n={2} titre="Situation de l'immeuble"
               aide="Désignez l'immeuble par son identifiant, sa référence cadastrale ou, à défaut, son lieu-dit.">
          <div className="ligne-champs">
            {/* ⚠️ C'EST ICI QUE LA SAISIE LIBRE A COÛTÉ LE PLUS CHER. Le
                23/09/2026, « Conakry / Conakry / Matoto / Tombola » a été
                déclaré pour GN-PAR-000000004, qui est à Boké-Centre : l'affaire
                est partie à 250 km de son fonds, et seule la RÉFÉRENCE
                (`AFF-MTO-…` sur un fonds de Boké) l'a rendue visible. */}
            <ChampsAdresse requis adresse={adresseDe(f, 'imm')}
                           onAdresse={(a) => {
                             pose('immRegion', a.region); pose('immPrefecture', a.prefecture);
                             pose('immCommune', a.commune); pose('immQuartier', a.quartier ?? '');
                           }} />
          </div>
          <div className="ligne-champs">
            <Champ label="Identifiant de parcelle" aide="GN-PAR-… si le requérant le connaît">
              <input value={f.immIdentifiant ?? ''} onChange={set('immIdentifiant')} />
            </Champ>
            <Champ label="Référence cadastrale"><input value={f.immReference ?? ''} onChange={set('immReference')} /></Champ>
            <Champ label="Lot de copropriété"><input value={f.immLot ?? ''} onChange={set('immLot')} /></Champ>
          </div>
          <Champ label="Lieu-dit / adresse de l'immeuble"
                 aide="à défaut d'identifiant : le repère qui permet de le retrouver sur le terrain">
            <input value={f.immLieuDit ?? ''} onChange={set('immLieuDit')} />
          </Champ>
        </Cadre>
      )}

      {type.code === 'extrait_cadastral' && (
        <>
          <Cadre n={3} titre="Personne inscrite au registre"
                 aide="À remplir si la demande porte sur le compte d'une personne plutôt que sur un bien.">
            <div className="ligne-champs">
              <Champ label="Nom"><input value={f.piNom ?? ''} onChange={set('piNom')} /></Champ>
              <Champ label="Prénoms"><input value={f.piPrenoms ?? ''} onChange={set('piPrenoms')} /></Champ>
              <Champ label="Date de naissance"><input type="date" value={f.piNaissance ?? ''} onChange={set('piNaissance')} /></Champ>
            </div>
            <div className="ligne-cases">
              <label className="case">
                <input type="checkbox" checked={Boolean(f.piSeul)}
                       onChange={(e) => setF((x) => ({ ...x, piSeul: e.target.checked }))} />
                Seule titulaire de droits sur le ou les biens
              </label>
              <label className="case">
                <input type="checkbox" checked={Boolean(f.piAutres)}
                       onChange={(e) => setF((x) => ({ ...x, piAutres: e.target.checked }))} />
                Titulaire avec d'autres personnes
              </label>
            </div>
          </Cadre>

          <Cadre n={4} titre="Objet de la demande">
            <div className="ligne-champs">
              <Champ label="Renseignement demandé" obligatoire>
                <select value={f.objet} onChange={set('objet')}>
                  <option value="releve_biens">Relevé des biens</option>
                  <option value="situation_juridique">Situation juridique</option>
                  <option value="plan_parcellaire">Plan parcellaire</option>
                  <option value="charges_et_servitudes">Charges et servitudes</option>
                </select>
              </Champ>
            </div>
            <Champ label="Motif de la demande" obligatoire aide="10 caractères minimum, consigné au registre">
              <textarea rows={2} value={f.motif ?? ''} onChange={set('motif')} required minLength={10} />
            </Champ>
          </Cadre>
        </>
      )}

      {type.code === 'immatriculation_fonciere' && (
        <Cadre n={3} titre="Origine de la détention"
               aide="Comment le requérant détient aujourd'hui : c'est ce que l'instruction vérifiera.">
          <div className="ligne-champs">
            <Champ label="Origine" obligatoire>
              <select value={f.origine ?? 'heritage'} onChange={set('origine')}>
                <option value="heritage">Héritage</option>
                <option value="achat_sous_seing">Achat sous seing privé</option>
                <option value="attribution_coutumiere">Attribution coutumière</option>
                <option value="attribution_administrative">Attribution administrative</option>
                <option value="donation">Donation</option>
                <option value="occupation_prolongee">Occupation prolongée</option>
                <option value="autre">Autre</option>
              </select>
            </Champ>
            <Champ label="Détient depuis"><input type="date" value={f.detientDepuis ?? ''} onChange={set('detientDepuis')} /></Champ>
          </div>
          <Champ label="Détail de l'origine" obligatoire aide="10 caractères minimum">
            <textarea rows={3} value={f.origineDetail ?? ''} onChange={set('origineDetail')} required minLength={10} />
          </Champ>
        </Cadre>
      )}

      {/* ── LA DONATION : DEUX PARTIES, UN DROIT, DEUX DÉCLARATIONS ──────
          ⚠️ L'ORDRE DES CADRES SUIT L'ACTE : qui donne, qui reçoit, ce qui est
          donné. Un formulaire qui demanderait « le bénéficiaire » après les
          charges ferait remplir l'accessoire avant l'essentiel. */}
      {type.code === 'donation' && (
        <>
          <Cadre n={3} titre="Celui qui donne — « donateur » à l'acte">
            <CadrePartie pose={pose} prefixe="don"
            repris={prefixePartie === 'don' ? !delie : undefined}
            onDelier={() => setDelie(true)} onRelier={() => setDelie(false)} f={f} set={set}
              aide="Il doit être titulaire inscrit au registre sur l'immeuble désigné. L'instruction le vérifiera : une donation par quelqu'un qui ne détient pas ne transmet rien." />
          </Cadre>

          <Cadre n={4} titre="Celui qui reçoit — « donataire » à l'acte">
            <CadrePartie pose={pose} prefixe="dat"
            repris={prefixePartie === 'dat' ? !delie : undefined}
            onDelier={() => setDelie(true)} onRelier={() => setDelie(false)} f={f} set={set}
              aide="Sans son acceptation, il n'y a pas de donation : il y a une offre. C'est la case obligatoire du cadre 6." />
          </Cadre>

          {/* ⚠️ L'ORIGINE AVANT CE QUI EST DONNÉ. On ne peut pas décrire ce
              qu'on transmet sans avoir dit d'où on le tient : l'ordre des
              cadres est celui du raisonnement, pas celui du schéma. */}
          <Cadre n={5} titre="D'où le donateur tient l'immeuble"
                 aide="Seul le mode est obligatoire. Le détail de l'acte d'origine est souvent inconnu d'un héritier — l'acte imprimera ce qui manque plutôt que de bloquer le dépôt.">
            <CadreOrigine f={f} set={set} />
          </Cadre>

          <Cadre n={6} titre="Ce qui est donné"
                 aide="La nature du droit et la part transmise. Le registre inscrira exactement ceci.">
            <div className="ligne-champs">
              <Champ label="Lien entre les parties" obligatoire
                     aide="il conditionne les droits d'enregistrement et la réserve héréditaire">
                <select value={f.lien ?? 'ascendant_descendant'} onChange={set('lien')}>
                  <option value="ascendant_descendant">Ascendant / descendant</option>
                  <option value="entre_epoux">Entre époux</option>
                  <option value="collateral">Collatéral (frère, sœur, neveu…)</option>
                  <option value="autre_famille">Autre lien familial</option>
                  <option value="aucun">Aucun lien de famille</option>
                </select>
              </Champ>
              <Champ label="Nature du droit donné" obligatoire
                     aide="la réserve d'usufruit est la forme la plus fréquente des transmissions familiales">
                <select value={f.natureDroit ?? 'propriete'} onChange={set('natureDroit')}>
                  <option value="propriete">Pleine propriété</option>
                  <option value="nue_propriete">Nue-propriété (le donateur garde l'usufruit)</option>
                  <option value="usufruit">Usufruit seul</option>
                </select>
              </Champ>
            </div>
            {/* ⚠️ UNE FRACTION ENTIÈRE, JAMAIS UN POURCENTAGE. `1/3` se dit
                `1/3` : trois donations de 33,3 % ne font pas un tout, et le
                tiers restant disparaîtrait du registre. */}
            <div className="ligne-champs">
              <Champ label="Part donnée — numérateur" obligatoire>
                <input type="number" min={1} step={1} value={f.quotiteNum ?? 1} onChange={set('quotiteNum')} required />
              </Champ>
              <Champ label="Dénominateur" obligatoire aide="1/1 = la totalité · 1/2 = la moitié · 1/3 = un tiers">
                <input type="number" min={1} step={1} value={f.quotiteDen ?? 1} onChange={set('quotiteDen')} required />
              </Champ>
            </div>
            <Champ label="Charges et conditions"
                   aide="facultatif — une donation peut être grevée : entretien, droit d'habitation, interdiction d'aliéner">
              <textarea rows={2} value={f.charges ?? ''} onChange={set('charges')} />
            </Champ>
          </Cadre>

          {/* ⚠️ LES TÉMOINS SONT FACULTATIFS ICI, EXIGÉS PAR L'AUTORITÉ QUI
              REÇOIT. Le registre ne sait pas si la commune du lieu en impose :
              il refuse de bloquer un dépôt pour une règle qu'il ignore, et
              l'acte imprime l'absence plutôt que de la taire. */}
          <Cadre n={7} titre="Témoins"
                 aide="Ils attestent la signature et l'identité des parties — rien d'autre. Un cadre laissé vide n'est pas envoyé : l'acte portera « aucun témoin recueilli ».">
            <CadreTemoins f={f} set={set} pose={pose} />
          </Cadre>

          <Cadre n={8} titre="Déclarations constitutives de l'acte"
                 aide="Ces deux cases ne sont pas des formalités : sans elles, l'acte déposé n'est pas une donation.">
            <label className="case case-forte">
              <input type="checkbox" checked={acceptation} onChange={(e) => setAcceptation(e.target.checked)} />
              Le donataire <strong>accepte</strong> la donation. Une donation est un contrat :
              sans acceptation, il n'y a qu'une offre, et rien ne se transmet.
            </label>
            <label className="case case-forte">
              <input type="checkbox" checked={declaration} onChange={(e) => setDeclaration(e.target.checked)} />
              Les parties déclarent qu'il n'existe <strong>aucune contrepartie</strong>.
              Une donation avec contrepartie est une VENTE : autre acte, autre assiette, autres droits.
            </label>
          </Cadre>
        </>
      )}

      {type.code === 'vente' && (
        <>
          <Cadre n={3} titre="Celui qui vend — « vendeur » à l'acte">
            <CadrePartie pose={pose} prefixe="ven"
            repris={prefixePartie === 'ven' ? !delie : undefined}
            onDelier={() => setDelie(true)} onRelier={() => setDelie(false)} f={f} set={set}
              aide="Il doit être titulaire inscrit au registre sur l'immeuble désigné. L'instruction le vérifiera : une vente par quelqu'un qui ne détient pas ne transmet rien." />
          </Cadre>

          <Cadre n={4} titre="Celui qui achète — « acquéreur » à l'acte">
            <CadrePartie pose={pose} prefixe="acq"
            repris={prefixePartie === 'acq' ? !delie : undefined}
            onDelier={() => setDelie(true)} onRelier={() => setDelie(false)} f={f} set={set}
              aide="Se vendre à soi-même est refusé : le registre y perdrait le RANG du droit, qui est ce qui départage deux prétendants." />
          </Cadre>

          <Cadre n={5} titre="D'où le vendeur tient l'immeuble"
                 aide="Seul le mode est obligatoire. Sans origine, l'acte nomme un vendeur sans pouvoir dire à quel titre il vend — et c'est précisément ce qu'une contestation attaque.">
            <CadreOrigine f={f} set={set} />
          </Cadre>

          <Cadre n={6} titre="Ce qui est vendu, et à quel prix"
                 aide="Le registre inscrira exactement la nature et la part indiquées ici.">
            <div className="ligne-champs">
              <Champ label="Nature du droit vendu" obligatoire>
                <select value={f.natureDroit ?? 'propriete'} onChange={set('natureDroit')}>
                  <option value="propriete">Pleine propriété</option>
                  <option value="nue_propriete">Nue-propriété (le vendeur garde l'usufruit)</option>
                  <option value="usufruit">Usufruit seul</option>
                </select>
              </Champ>
              <Champ label="Part vendue — numérateur" obligatoire>
                <input type="number" min={1} step={1} value={f.quotiteNum ?? 1} onChange={set('quotiteNum')} required />
              </Champ>
              <Champ label="Dénominateur" obligatoire aide="1/1 = la totalité · 1/2 = la moitié">
                <input type="number" min={1} step={1} value={f.quotiteDen ?? 1} onChange={set('quotiteDen')} required />
              </Champ>
            </div>
            {/* ⚠️ UN PRIX À ZÉRO N'EST PAS UNE VENTE : c'est une donation,
                sous un autre régime fiscal et sans la déclaration de gratuité
                qui protège les héritiers réservataires. Le schéma l'exige
                strictement positif ; le champ le dit plutôt que de laisser
                l'agent le découvrir au dépôt. */}
            <div className="ligne-champs">
              <Champ label="Prix (GNF)" obligatoire
                     aide="strictement positif : une vente à zéro est une donation, autre acte et autre assiette">
                <input type="number" min={1} step={1} value={f.prix ?? ''} onChange={set('prix')} required />
              </Champ>
              <Champ label="Entrée en jouissance" aide="« immédiate », « au 1er janvier », …">
                <input value={f.jouissance ?? ''} onChange={set('jouissance')} />
              </Champ>
            </div>
            <Champ label="Modalités de paiement" obligatoire
                   aide="comptant, virement, échelonné — trois caractères au minimum">
              <input value={f.modalites ?? ''} onChange={set('modalites')} required minLength={3} />
            </Champ>
            {/* ⚠️ « QUITTANCE » N'EST PAS « PRIX ». Convenir d'un prix n'est pas
                l'avoir reçu : décoché vaut « non payé », qui est licite (vente
                à terme) et n'est pas un silence. */}
            <label className="case">
              <input type="checkbox" checked={Boolean(f.quittance)}
                     onChange={(e) => setF((x) => ({ ...x, quittance: e.target.checked }))} />
              <strong>Quittance</strong> : le vendeur reconnaît avoir reçu l'intégralité du prix.
              Laissé décoché, l'acte portera une vente dont le prix reste dû.
            </label>
            <label className="case">
              <input type="checkbox" checked={f.eviction !== false}
                     onChange={(e) => setF((x) => ({ ...x, eviction: e.target.checked }))} />
              Le vendeur donne la <strong>garantie d'éviction</strong> et du fait personnel.
            </label>
            <Champ label="Charges et conditions"
                   aide="facultatif — occupation, servitude, condition suspensive…">
              <textarea rows={2} value={f.charges ?? ''} onChange={set('charges')} />
            </Champ>
          </Cadre>

          <Cadre n={7} titre="Témoins"
                 aide="Ils attestent la signature et l'identité des parties — rien d'autre. Un cadre laissé vide n'est pas envoyé.">
            <CadreTemoins f={f} set={set} pose={pose} />
          </Cadre>

          <Cadre n={8} titre="Déclaration constitutive de l'acte"
                 aide="La minoration du prix est LA fraude classique de la vente immobilière : elle prive l'État de ses droits et le vendeur de son recours.">
            <label className="case case-forte">
              <input type="checkbox" checked={declaration} onChange={(e) => setDeclaration(e.target.checked)} />
              Les parties déclarent la <strong>sincérité du prix</strong> stipulé.
              Si le prix stipulé n'est pas le prix réel, l'acte ne peut pas être reçu.
            </label>
          </Cadre>
        </>
      )}

      {type.code === 'mainlevee' && (
        <>
          {/* ⚠️ LE CRÉANCIER D'ABORD, ET L'ORDRE EST LA RÈGLE : c'est celui qui
              se dessaisit qui ouvre l'acte. Le débiteur a le plus grand intérêt
              à la mainlevée et aucun pouvoir de la donner. */}
          <Cadre n={3} titre="Le créancier — celui qui libère le fonds">
            <CadrePartie pose={pose} prefixe="cre"
            repris={prefixePartie === 'cre' ? !delie : undefined}
            onDelier={() => setDelie(true)} onRelier={() => setDelie(false)} f={f} set={set}
              aide="La mainlevée émane de lui, et de lui seul. Un propriétaire ne lève pas l'hypothèque de sa propre banque." />
          </Cadre>

          <Cadre n={4} titre="Le débiteur — celui dont le fonds est grevé">
            <CadrePartie pose={pose} prefixe="deb"
            repris={prefixePartie === 'deb' ? !delie : undefined}
            onDelier={() => setDelie(true)} onRelier={() => setDelie(false)} f={f} set={set}
              aide="Il bénéficie de la radiation ; il ne la consent pas." />
          </Cadre>

          <Cadre n={5} titre="La sûreté dont mainlevée est donnée"
                 aide="Le registre peut porter plusieurs sûretés sur le même fonds, du même créancier et au même rang : désignez celle qui tombe.">
            <div className="ligne-champs">
              <Champ label="Nature de la sûreté" obligatoire>
                <select value={f.natureSurete ?? 'hypotheque'} onChange={set('natureSurete')}>
                  {NATURES_SURETE.map((v) => <option key={v} value={v}>{LIBELLE_SURETE[v]}</option>)}
                </select>
              </Champ>
              <Champ label="Référence de l'inscription" obligatoire
                     aide="le numéro qui figure sur l'acte du créancier — pas un identifiant interne">
                <input value={f.refInscription ?? ''} onChange={set('refInscription')} required />
              </Champ>
              <Champ label="Rang"><input type="number" min={1} value={f.rangInscription ?? ''} onChange={set('rangInscription')} /></Champ>
              <Champ label="Montant garanti (GNF)">
                <input type="number" min={1} value={f.montantGaranti ?? ''} onChange={set('montantGaranti')} />
              </Champ>
            </div>
          </Cadre>

          <Cadre n={6} titre="Pourquoi la sûreté tombe"
                 aide="Un tiers qui lira le registre dans vingt ans doit savoir si la dette a été payée, remise, prescrite, ou si l'inscription était nulle : les quatre se plaident différemment.">
            <div className="ligne-champs">
              <Champ label="Cause de l'extinction" obligatoire>
                <select required value={f.causeExtinction ?? ''} onChange={set('causeExtinction')}>
                  <option value="">— choisir —</option>
                  {CAUSES_EXTINCTION.map((v) => <option key={v} value={v}>{LIBELLE_CAUSE_EXTINCTION[v]}</option>)}
                </select>
              </Champ>
              <Champ label="Précisions"
                     aide="obligatoires si la cause est « autre » : une radiation sans cause lisible se conteste">
                <input value={f.causeDetail ?? ''} onChange={set('causeDetail')} />
              </Champ>
            </div>
            {/* ⚠️ « PAYÉ INTÉGRALEMENT » SANS QUITTANCE EST UNE CONTRADICTION,
                et elle se retourne contre le débiteur : le créancier pourrait
                réclamer la même somme, sûreté en moins mais créance intacte. */}
            <label className="case">
              <input type="checkbox" checked={Boolean(f.quittance)}
                     onChange={(e) => setF((x) => ({ ...x, quittance: e.target.checked }))} />
              Le créancier déclare avoir été <strong>intégralement payé</strong>.
            </label>
          </Cadre>

          <Cadre n={7} titre="Témoins"
                 aide="Un cadre laissé vide n'est pas envoyé : l'acte portera « aucun témoin recueilli ».">
            <CadreTemoins f={f} set={set} pose={pose} />
          </Cadre>

          <Cadre n={8} titre="Consentement du créancier"
                 aide="Sans lui, l'acte reçu n'est pas une mainlevée : c'est une demande adressée au créancier.">
            <label className="case case-forte">
              <input type="checkbox" checked={acceptation} onChange={(e) => setAcceptation(e.target.checked)} />
              Le créancier <strong>consent expressément à la radiation</strong> de l'inscription désignée.
            </label>
          </Cadre>
        </>
      )}

      {/* ═══ L'ÉCHANGE — deux patrimoines, deux fonds, un seul contrat ═══
          ⚠️ CHACUN EST À LA FOIS CÉDANT ET BÉNÉFICIAIRE : les cadres disent ce
          que chacun DONNE et ce qu'il REÇOIT, jamais « cédant »/« acquéreur »,
          qui seraient faux de moitié dans les deux sens. */}
      {type.code === 'echange' && (
        <>
          <Cadre n={3} titre="Le premier copermutant — il apporte le fonds du cadre 2">
            <CadrePartie pose={pose} prefixe="cp1"
            repris={prefixePartie === 'cp1' ? !delie : undefined}
            onDelier={() => setDelie(true)} onRelier={() => setDelie(false)} f={f} set={set}
              aide="Il cède le fonds décrit plus haut et reçoit celui du cadre 5." />
          </Cadre>

          <Cadre n={4} titre="Le second copermutant — il apporte le fonds du cadre 5">
            <CadrePartie pose={pose} prefixe="cp2"
            repris={prefixePartie === 'cp2' ? !delie : undefined}
            onDelier={() => setDelie(true)} onRelier={() => setDelie(false)} f={f} set={set}
              aide="Il cède le fonds du cadre 5 et reçoit celui du cadre 2." />
          </Cadre>

          {/* ⚠️ LE SECOND FONDS A SES PROPRES CHAMPS. C'est le seul endroit du
              guichet où deux biens coexistent : réutiliser le cadre commun
              aurait écrasé le premier, en silence. */}
          <Cadre n={5} titre="Le second immeuble — celui qu'apporte le second copermutant"
                 aide="Un échange sans second fonds n'est pas un échange : ce cadre est obligatoire.">
            <div className="ligne-champs">
              {/* ⚠️ MÊME CASCADE QUE LE PREMIER FONDS. Ce cadre avait échappé au
                  premier passage parce qu'il ne portait PAS la liste `REGIONS` :
                  il n'avait aucun champ de région du tout, et ses deux échelons
                  étaient libres. Chercher « REGIONS » l'aurait donc déclaré
                  propre — c'est un essai qui l'a attrapé, pas une relecture. */}
              <ChampsAdresse requis adresse={adresseDe(f, 'im2')}
                             onAdresse={(a) => {
                               pose('im2Region', a.region); pose('im2Prefecture', a.prefecture);
                               pose('im2Commune', a.commune); pose('im2Quartier', a.quartier ?? '');
                             }} />
              <Champ label="Lieu-dit"><input value={f.im2LieuDit ?? ''} onChange={set('im2LieuDit')} /></Champ>
              <Champ label="Identifiant de parcelle" aide="GN-PAR-… s'il est connu">
                <input value={f.im2Identifiant ?? ''} onChange={set('im2Identifiant')} />
              </Champ>
              <Champ label="Référence cadastrale">
                <input value={f.im2Reference ?? ''} onChange={set('im2Reference')} />
              </Champ>
            </div>
          </Cadre>

          <Cadre n={6} titre="Les valeurs et la soulte"
                 aide="La soulte rétablit l'équilibre quand les lots ne se valent pas. Elle est facultative — mais une soulte sans débiteur n'est pas une obligation.">
            <div className="ligne-champs">
              <Champ label="Valeur du premier lot (GNF)">
                <input type="number" min={1} value={f.valeurLot1 ?? ''} onChange={set('valeurLot1')} />
              </Champ>
              <Champ label="Valeur du second lot (GNF)">
                <input type="number" min={1} value={f.valeurLot2 ?? ''} onChange={set('valeurLot2')} />
              </Champ>
              <Champ label="Soulte (GNF)">
                <input type="number" min={1} value={f.soulteMontant ?? ''} onChange={set('soulteMontant')} />
              </Champ>
              <Champ label="Qui doit la soulte"
                     aide="obligatoire dès qu'un montant est saisi">
                <select value={f.soulteDebiteur ?? ''} onChange={set('soulteDebiteur')}>
                  <option value="">— aucune soulte —</option>
                  <option value="premier">Le premier copermutant</option>
                  <option value="second">Le second copermutant</option>
                </select>
              </Champ>
              <Champ label="Date de l'acte d'échange">
                <input type="date" value={f.dateActeEchange ?? ''} onChange={set('dateActeEchange')} />
              </Champ>
            </div>
            {/* ⚠️ SANS SOULTE ET AVEC DES LOTS TRÈS INÉGAUX, L'ACTE EST
                REQUALIFIABLE EN DONATION DÉGUISÉE. Le registre ne peut pas
                évaluer les biens ; il peut exiger que les parties déclarent. */}
            <label className="case case-forte">
              <input type="checkbox" checked={Boolean(f.lotsEgaux)}
                     onChange={(e) => setF((x) => ({ ...x, lotsEgaux: e.target.checked }))} />
              Les parties déclarent les deux lots de <strong>valeur équivalente</strong>,
              soulte comprise s'il y en a une.
            </label>
          </Cadre>

          <Cadre n={7} titre="Témoins"
                 aide="Un cadre laissé vide n'est pas envoyé : l'acte portera « aucun témoin recueilli ».">
            <CadreTemoins f={f} set={set} pose={pose} />
          </Cadre>
        </>
      )}

      {/* ═══ L'INSCRIPTION DE SÛRETÉ — le miroir ENTRANT de la mainlevée ═══
          ⚠️ LE CONSTITUANT D'ABORD, ET C'EST L'INVERSE DE LA MAINLEVÉE : celui
          qui se dessaisit ouvre l'acte. Ici, c'est le propriétaire qui grève
          son fonds ; le créancier reçoit. */}
      {type.code === 'inscription_surete' && (
        <>
          <Cadre n={3} titre="Le constituant — celui dont le fonds est grevé">
            <CadrePartie pose={pose} prefixe="cst"
            repris={prefixePartie === 'cst' ? !delie : undefined}
            onDelier={() => setDelie(true)} onRelier={() => setDelie(false)} f={f} set={set}
              aide="Seul le propriétaire peut grever son fonds. Une banque n'inscrit pas d'elle-même : elle reçoit." />
          </Cadre>

          <Cadre n={4} titre="Le créancier — celui au profit de qui la sûreté est inscrite">
            <CadrePartie pose={pose} prefixe="cre"
            repris={prefixePartie === 'cre' ? !delie : undefined}
            onDelier={() => setDelie(true)} onRelier={() => setDelie(false)} f={f} set={set}
              aide="Il bénéficie de l'inscription ; il ne la consent pas." />
          </Cadre>

          <Cadre n={5} titre="La sûreté inscrite, et son rang"
                 aide="Le rang décide qui sera payé le premier le jour d'une vente forcée. C'est la mention la plus lourde de conséquences de cet acte.">
            <div className="ligne-champs">
              <Champ label="Nature de la sûreté" obligatoire>
                <select value={f.natureSurete ?? 'hypotheque'} onChange={set('natureSurete')}>
                  {NATURES_SURETE.map((v) => <option key={v} value={v}>{LIBELLE_SURETE[v]}</option>)}
                </select>
              </Champ>
              {/* ⚠️ OBLIGATOIRE, à la différence de la mainlevée où le rang
                  RAPPELLE une inscription existante : ici, il la CRÉE. */}
              <Champ label="Rang demandé" obligatoire
                     aide="1 = premier payé. Un registre qui accepte une hypothèque sans rang ne sait plus dans quel ordre les créanciers viennent.">
                <input type="number" min={1} value={f.rangDemande ?? ''} onChange={set('rangDemande')} required />
              </Champ>
              <Champ label="Montant garanti (GNF)" obligatoire>
                <input type="number" min={1} value={f.montantGaranti ?? ''} onChange={set('montantGaranti')} required />
              </Champ>
            </div>
          </Cadre>

          <Cadre n={6} titre="La créance garantie"
                 aide="Une sûreté sans cause ne garantit rien : le tiers qui lira le registre doit savoir CE QUI est garanti, et par quel titre.">
            <div className="ligne-champs">
              <Champ label="Cause de la créance" obligatoire
                     aide="prêt, ouverture de crédit, solde de prix de vente…">
                <input value={f.causeCreance ?? ''} onChange={set('causeCreance')} required minLength={3} />
              </Champ>
              <Champ label="Titre constitutif" obligatoire
                     aide="l'acte, la convention ou le jugement qui crée la créance">
                <input value={f.titreConstitutif ?? ''} onChange={set('titreConstitutif')} required minLength={2} />
              </Champ>
              <Champ label="Date du titre" obligatoire>
                <input type="date" value={f.dateTitreConstitutif ?? ''} onChange={set('dateTitreConstitutif')} required />
              </Champ>
            </div>
          </Cadre>

          <Cadre n={7} titre="Témoins"
                 aide="Un cadre laissé vide n'est pas envoyé : l'acte portera « aucun témoin recueilli ».">
            <CadreTemoins f={f} set={set} pose={pose} />
          </Cadre>

          <Cadre n={8} titre="Consentement du constituant"
                 aide="Sans lui, l'acte reçu n'est pas une inscription : c'est une demande adressée au propriétaire du fonds.">
            <label className="case case-forte">
              <input type="checkbox" checked={acceptation} onChange={(e) => setAcceptation(e.target.checked)} />
              Le constituant <strong>consent expressément à l'inscription</strong> de la sûreté sur son fonds.
            </label>
          </Cadre>
        </>
      )}

      {/* ═══ LA RADIATION — elle CONSTATE, elle ne se consent pas ═══
          ⚠️⚠️ AUCUNE CASE DE CONSENTEMENT DANS CE FORMULAIRE, et c'est le
          point. La mainlevée émane du créancier ; la radiation est opérée par
          le conservateur sur une cause extérieure. Une case « consentez-vous »
          sur une radiation fondée sur un jugement ferait croire qu'un
          créancier peut s'y opposer. Ce qui la fonde est la RÉFÉRENCE. */}
      {type.code === 'radiation' && (
        <>
          <Cadre n={3} titre="Le titulaire de l'inscription radiée">
            <CadrePartie pose={pose} prefixe="tit"
            repris={prefixePartie === 'tit' ? !delie : undefined}
            onDelier={() => setDelie(true)} onRelier={() => setDelie(false)} f={f} set={set}
              aide="Celui au profit de qui l'inscription avait été prise. Sa radiation ne dépend pas de son accord : elle constate une cause." />
          </Cadre>

          <Cadre n={4} titre="Le propriétaire du fonds apuré">
            <CadrePartie pose={pose} prefixe="prp"
            repris={prefixePartie === 'prp' ? !delie : undefined}
            onDelier={() => setDelie(true)} onRelier={() => setDelie(false)} f={f} set={set}
              aide="Celui dont le fonds est libéré par la radiation." />
          </Cadre>

          <Cadre n={5} titre="L'inscription dont la radiation est requise"
                 aide="Le registre peut porter plusieurs inscriptions du même créancier au même rang : désignez celle qui tombe.">
            <div className="ligne-champs">
              <Champ label="Nature de l'inscription">
                <select value={f.natureSurete ?? ''} onChange={set('natureSurete')}>
                  <option value="">— non précisée —</option>
                  {NATURES_SURETE.map((v) => <option key={v} value={v}>{LIBELLE_SURETE[v]}</option>)}
                </select>
              </Champ>
              <Champ label="Référence de l'inscription" obligatoire
                     aide="le numéro qui figure au registre — pas un identifiant interne">
                <input value={f.refInscription ?? ''} onChange={set('refInscription')} required />
              </Champ>
              <Champ label="Rang"><input type="number" min={1} value={f.rangInscription ?? ''} onChange={set('rangInscription')} /></Champ>
            </div>
          </Cadre>

          <Cadre n={6} titre="Ce qui fonde la radiation"
                 aide="La radiation ne se consent pas : elle constate. Sans acte ni décision à citer, elle efface une garantie sur parole.">
            <div className="ligne-champs">
              <Champ label="Cause" obligatoire>
                <select required value={f.causeRadiation ?? ''} onChange={set('causeRadiation')}>
                  <option value="">— choisir —</option>
                  {CAUSES_EXTINCTION.map((v) => <option key={v} value={v}>{LIBELLE_CAUSE_EXTINCTION[v]}</option>)}
                </select>
              </Champ>
              <Champ label="Référence de l'acte ou de la décision" obligatoire
                     aide="mainlevée, jugement, décision administrative…">
                <input value={f.referenceCause ?? ''} onChange={set('referenceCause')} required minLength={2} />
              </Champ>
              <Champ label="Date" obligatoire>
                <input type="date" value={f.dateCause ?? ''} onChange={set('dateCause')} required />
              </Champ>
              <Champ label="Précisions"
                     aide="obligatoires si la cause est « autre »">
                <input value={f.causeDetail ?? ''} onChange={set('causeDetail')} />
              </Champ>
            </div>
          </Cadre>

          <Cadre n={7} titre="Étendue de la radiation"
                 aide="Une radiation partielle laisse la sûreté vivante pour le reste. Un tiers qui lit « radiée » sans cette nuance croit le fonds libre, et prête dessus.">
            <label className="case case-forte">
              <input type="checkbox" checked={Boolean(f.radiationTotale)}
                     onChange={(e) => setF((x) => ({ ...x, radiationTotale: e.target.checked }))} />
              La radiation porte sur la <strong>totalité</strong> de l'inscription.
            </label>
            {!f.radiationTotale && (
              <Champ label="Montant radié (GNF)" obligatoire
                     aide="à concurrence de quel montant l'inscription tombe — sans lui, nul ne sait ce qui reste inscrit">
                <input type="number" min={1} value={f.montantRadie ?? ''} onChange={set('montantRadie')} required />
              </Champ>
            )}
          </Cadre>
        </>
      )}

      {type.code === 'succession' && (
        <>
          <Cadre n={3} titre="Le défunt"
                 aide="La personne dont la succession s'ouvre. Elle doit figurer au registre foncier : sinon il n'y a rien à transmettre PAR CE REGISTRE — ce qui ne veut pas dire qu'elle ne possédait rien.">
            {/**
              * ⚠️ NI TÉLÉPHONE NI PIÈCE OBLIGATOIRE DANS CE CADRE, à la
              * différence de toutes les autres parties du guichet. Les
              * réclamer pour un mort fermerait la porte au cas le plus
              * fréquent : l'héritier qui vient déclarer le décès de son père
              * n'a ni sa carte, ni son numéro.
              */}
            <div className="ligne-champs">
              <Champ label="Nom" obligatoire><input value={f.dcNom ?? ''} onChange={set('dcNom')} required /></Champ>
              <Champ label="Prénoms" obligatoire><input value={f.dcPrenoms ?? ''} onChange={set('dcPrenoms')} required /></Champ>
            </div>
            <div className="ligne-champs">
              {/* ⚠️ FACULTATIVE — l'enregistrement des naissances n'est pas
                  universel, et un défunt né dans les années 1940 n'en a
                  souvent pas. L'exiger bloquerait le dossier. */}
              <Champ label="Date de naissance" aide="si connue — beaucoup de défunts n'ont pas d'acte de naissance">
                <input type="date" value={f.dcNaissance ?? ''} onChange={set('dcNaissance')} />
              </Champ>
              <Champ label="Lieu de naissance"><input value={f.dcLieuNaissance ?? ''} onChange={set('dcLieuNaissance')} /></Champ>
              <Champ label="Nationalité"><input value={f.dcNationalite ?? ''} onChange={set('dcNationalite')} placeholder="Guinéenne" /></Champ>
              <Champ label="Profession"><input value={f.dcProfession ?? ''} onChange={set('dcProfession')} /></Champ>
            </div>
            <div className="ligne-champs">
              <Champ label="Pièce d'identité du défunt" aide="facultative : elle est souvent perdue ou restituée">
                <select value={f.dcPieceType ?? ''} onChange={set('dcPieceType')}>
                  <option value="">— non produite —</option>
                  {PIECES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </Champ>
              <Champ label="Numéro"><input value={f.dcPieceNumero ?? ''} onChange={set('dcPieceNumero')} /></Champ>
              {/* ⚠️ FACULTATIF POUR UN MORT — même motif que le téléphone et la
                  pièce : l'héritier ne l'a pas. Mais s'il est fourni, il est
                  VÉRIFIÉ : un NIN mal formé sur un défunt relierait le
                  patrimoine à la mauvaise personne, et personne ne le relira. */}
              <Champ label="Identifiant national (NIN)" aide="facultatif pour un défunt — vérifié s'il est fourni">
                <ChampNin valeur={f.dcNin ?? ''} onValeur={(v) => pose('dcNin', v)} requis={false}
                          dateNaissance={f.dcNaissance}
                          onDateDeduite={(iso) => pose('dcNaissance', iso)}
                          lieuNaissance={f.dcLieuNaissance}
                          onLieuDeduit={(nom) => pose('dcLieuNaissance', nom)} />
              </Champ>
            </div>
            {/**
              * ⚠️ LA SITUATION MATRIMONIALE DU DÉFUNT N'EST PAS DÉCORATIVE : le
              * conjoint survivant est un ayant droit, et le régime décide de ce
              * qui entre dans la succession. Un acte qui la tait laisse
              * l'instruction décider sans savoir.
              */}
            <div className="ligne-champs">
              <Champ label="Situation matrimoniale au décès"
                     aide="le conjoint survivant est un ayant droit — déclarez-le aussi au cadre 4">
                <select value={f.dcSituation ?? ''} onChange={set('dcSituation')}>
                  <option value="">Non déclarée</option>
                  <option value="celibataire">Célibataire</option>
                  <option value="marie">Marié(e)</option>
                  <option value="veuf">Veuf / Veuve</option>
                  <option value="divorce">Divorcé(e)</option>
                  <option value="separe">Séparé(e)</option>
                </select>
              </Champ>
              <Champ label="Nom du conjoint"><input value={f.dcConjointNom ?? ''} onChange={set('dcConjointNom')} /></Champ>
            </div>

            <fieldset className="sous-cadre">
              <legend>Filiation du défunt — elle départage les homonymes du registre</legend>
              {(['Pere', 'Mere'] as const).map((lien) => (
                <div key={lien} className="ligne-champs">
                  <Champ label={`${lien === 'Pere' ? 'Père' : 'Mère'} — nom`}>
                    <input value={f[`dc${lien}Nom`] ?? ''} onChange={set(`dc${lien}Nom`)} />
                  </Champ>
                  <Champ label="Prénoms">
                    <input value={f[`dc${lien}Prenoms`] ?? ''} onChange={set(`dc${lien}Prenoms`)} />
                  </Champ>
                  <Champ label="Statut">
                    <select value={f[`dc${lien}Statut`] ?? ''} onChange={set(`dc${lien}Statut`)}>
                      <option value="">Non déclaré</option>
                      <option value="vivant">Vivant(e)</option>
                      <option value="decede">Décédé(e)</option>
                    </select>
                  </Champ>
                </div>
              ))}
            </fieldset>

            <fieldset className="sous-cadre">
              <legend>Dernier domicile — c'est lui qui rattache la succession à un ressort</legend>
              <div className="ligne-champs">
                <ChampsAdresse requis adresse={adresseDe(f, 'dc')}
                               onAdresse={(a) => {
                                 pose('dcRegion', a.region); pose('dcPrefecture', a.prefecture);
                                 pose('dcCommune', a.commune); pose('dcQuartier', a.quartier ?? '');
                               }} />
              </div>
            </fieldset>
          </Cadre>

          <Cadre n={4} titre="Les ayants droit déclarés"
                 aide="Ce cadre recueille une DÉCLARATION, pas une dévolution. L'inscription au registre passe par l'instruction et engage l'État : un héritier omis ici découvrirait son éviction en consultant le livre foncier.">
            {Array.from({ length: nbHeritiers }, (_, i) => `h${i}`).map((prefixe, i) => (
              <fieldset key={prefixe} className="sous-cadre">
                <legend>Ayant droit n° {i + 1}</legend>
                <div className="ligne-champs">
                  <Champ label="Nom" obligatoire={i === 0}>
                    <input value={f[`${prefixe}Nom`] ?? ''} onChange={set(`${prefixe}Nom`)} required={i === 0} />
                  </Champ>
                  <Champ label="Prénoms" obligatoire={i === 0}>
                    <input value={f[`${prefixe}Prenoms`] ?? ''} onChange={set(`${prefixe}Prenoms`)} required={i === 0} />
                  </Champ>
                  <Champ label="Qualité" obligatoire
                         aide="elle décide de la vocation, et du passif qu'elle emporte">
                    <select value={f[`${prefixe}Qualite`] ?? 'descendant'} onChange={set(`${prefixe}Qualite`)}>
                      {QUALITES_SUCCESSORALES.map((q) => (
                        <option key={q} value={q}>{LIBELLE_QUALITE[q]}</option>
                      ))}
                    </select>
                  </Champ>
                </div>
                <div className="ligne-champs">
                  <Champ label="Lien" aide="« fils aîné », « deuxième épouse »">
                    <input value={f[`${prefixe}Lien`] ?? ''} onChange={set(`${prefixe}Lien`)} />
                  </Champ>
                  <Champ label="Date de naissance">
                    <input type="date" value={f[`${prefixe}Naissance`] ?? ''} onChange={set(`${prefixe}Naissance`)} />
                  </Champ>
                  <Champ label="Téléphone">
                    <input value={f[`${prefixe}Telephone`] ?? ''} onChange={set(`${prefixe}Telephone`)} placeholder="+224…" />
                  </Champ>
                </div>
                {/**
                  * ⚠️ LA VOCATION EST FACULTATIVE AU GUICHET, ET ELLE DOIT
                  * L'ÊTRE. Un requérant sait qui hérite ; il ne sait pas
                  * toujours dans quelles proportions — cela dépend du droit
                  * applicable, des souches et du testament. Exiger une fraction
                  * ici ferait inventer un chiffre à quelqu'un qui n'a pas à le
                  * connaître, et ce chiffre finirait sur un acte.
                  *
                  * ⚠️ EN FRACTION, JAMAIS EN POURCENTAGE : trois héritiers à
                  * 33,3 % ne font pas le tout, et le tiers manquant
                  * n'appartiendrait à personne.
                  */}
                <div className="ligne-champs">
                  <Champ label="Part — numérateur" aide="facultative : la dévolution se fixe souvent à l'instruction">
                    <input type="number" min={1} value={f[`${prefixe}Num`] ?? ''} onChange={set(`${prefixe}Num`)} />
                  </Champ>
                  <Champ label="Part — dénominateur">
                    <input type="number" min={1} value={f[`${prefixe}Den`] ?? ''} onChange={set(`${prefixe}Den`)} />
                  </Champ>
                </div>
              </fieldset>
            ))}
            <div className="actions">
              <button type="button" className="bouton discret" disabled={nbHeritiers >= 30}
                      onClick={() => setNbHeritiers((n) => Math.min(30, n + 1))}>
                Ajouter un ayant droit
              </button>
              {nbHeritiers > 1 && (
                <button type="button" className="bouton discret"
                        onClick={() => setNbHeritiers((n) => Math.max(1, n - 1))}>
                  Retirer le dernier cadre
                </button>
              )}
            </div>
          </Cadre>

          <Cadre n={5} titre="Ce qui établit le décès, et ce qui établit la dévolution"
                 aide="Deux démonstrations distinctes, et on ne les confond jamais : un acte de décès ne dit rien des enfants, un acte de notoriété ne prouve pas la mort.">
            <div className="ligne-champs">
              <Champ label="Date du décès" obligatoire
                     aide="elle fixe le point de départ des droits des héritiers — pas la date du partage">
                <input type="date" required max={new Date().toISOString().slice(0, 10)}
                       value={f.dcDateDeces ?? ''} onChange={set('dcDateDeces')} />
              </Champ>
              <Champ label="Lieu du décès" obligatoire aide="c'est là qu'on ira chercher l'acte de décès">
                <input value={f.dcLieuDeces ?? ''} onChange={set('dcLieuDeces')} required />
              </Champ>
            </div>
            {/**
              * ⚠️ AUCUNE DES DEUX SOURCES N'EST PRÉ-SÉLECTIONNÉE, et c'est une
              * règle. Pré-cocher « acte de décès » ferait porter au registre
              * qu'un acte existe quand personne ne l'a vu — et c'est sur cette
              * mention qu'un tiers s'appuiera dans dix ans pour dire que la
              * mort était établie.
              */}
            <div className="ligne-champs">
              <Champ label="Le décès est établi par" obligatoire>
                <select required value={f.sourceDeces ?? ''} onChange={set('sourceDeces')}>
                  <option value="">— choisir —</option>
                  {SOURCES_DECES.map((v) => <option key={v} value={v}>{LIBELLE_SOURCE_DECES[v]}</option>)}
                </select>
              </Champ>
              <Champ label="Référence de l'acte de décès"
                     aide="obligatoire si vous déclarez un acte de décès : une mention que rien ne désigne ne se vérifie pas, et se présente pourtant comme une preuve">
                <input value={f.refActeDeces ?? ''} onChange={set('refActeDeces')} />
              </Champ>
            </div>
            <div className="ligne-champs">
              <Champ label="La dévolution est établie par" obligatoire>
                <select required value={f.sourceDevolution ?? ''} onChange={set('sourceDevolution')}>
                  <option value="">— choisir —</option>
                  {SOURCES_DEVOLUTION.map((v) => <option key={v} value={v}>{LIBELLE_SOURCE_DEVOLUTION[v]}</option>)}
                </select>
              </Champ>
              <Champ label="Référence de l'acte de notoriété, du jugement ou du testament"
                     aide="obligatoire pour ces trois-là : sans elle, l'instruction ne peut pas aller le chercher">
                <input value={f.refDevolution ?? ''} onChange={set('refDevolution')} />
              </Champ>
            </div>
            {/**
              * ⚠️ CE QUE CE FORMULAIRE NE DEMANDE PAS, ET POURQUOI. L'option
              * successorale ne se recueille pas au guichet : elle s'exerce
              * héritier par héritier, après connaissance du passif. Le dire
              * évite qu'un agent la cherche et conclue qu'elle a été oubliée.
              */}
            <p className="cadre-aide">
              L'option de chaque héritier — accepter, accepter à concurrence de l'actif net,
              renoncer — ne se recueille pas ici : elle s'exerce après connaissance du passif,
              sur la fiche de succession. Le silence ne vaut pas acceptation.
            </p>
          </Cadre>
        </>
      )}

      {type.code === 'duplicata_titre' && (
        <Cadre n={2} titre="Titre concerné et circonstances">
          <div className="ligne-champs">
            <Champ label="Identifiant du titre" obligatoire>
              <input value={f.identifiantTitre ?? ''} onChange={set('identifiantTitre')} placeholder="GN-TIT-…" required />
            </Champ>
            <Champ label="Cause" obligatoire>
              <select value={f.cause ?? 'perte'} onChange={set('cause')}>
                <option value="perte">Perte</option><option value="vol">Vol</option>
                <option value="destruction">Destruction</option><option value="deterioration">Détérioration</option>
              </select>
            </Champ>
          </div>
          <Champ label="Circonstances" obligatoire aide="20 caractères minimum — le duplicata en portera la mention">
            <textarea rows={3} value={f.circonstances ?? ''} onChange={set('circonstances')} required minLength={20} />
          </Champ>
          <label className="case">
            <input type="checkbox" checked={declaration} onChange={(e) => setDeclaration(e.target.checked)} />
            Je déclare sur l'honneur l'exactitude des circonstances ci-dessus.
          </label>
        </Cadre>
      )}

      {/* ⚠️ LES RÈGLES D'UTILISATION SONT IMPRIMÉES, PAS RÉSUMÉES. Les données
          cadastrales nomment des personnes : les délivrer sans engagement ferait
          du registre la source du démarchage qu'il doit empêcher. */}
      {/**
        * ⚠️ LA NUMÉROTATION ÉTAIT FAUSSE POUR LA DONATION, et sur une pièce
        * administrative un numéro de cadre n'est pas décoratif : c'est ce que
        * l'agent cite au téléphone (« il manque le cadre 6 »). La donation
        * portait DEUX cadres n° 7 — Témoins, puis celui-ci — et son cadre 8
        * s'affichait AVANT le 7. L'immatriculation sautait le 4. Mesuré en
        * ajoutant la vente : les deux se voient d'un coup d'œil sur la liste
        * des `<Cadre n={…}>`, jamais en lisant le formulaire à l'écran, où
        * chaque cadre paraît juste isolément.
        */}
      <Cadre n={type.code === 'duplicata_titre' ? 3
                : type.code === 'immatriculation_fonciere' ? 4
                : type.code === 'succession' ? 6
                : type.code === 'radiation' ? 8
                : type.code === 'echange' ? 8
                : ['donation', 'vente', 'mainlevee', 'inscription_surete'].includes(type.code) ? 9 : 5}
             titre="Règles d'utilisation et de confidentialité">
        <ul className="regles">
          <li>La réutilisation des informations cadastrales est limitée à l'objet déclaré ci-dessus.</li>
          <li>Tout traitement ultérieur de données à caractère personnel engage la responsabilité du demandeur.</li>
          <li>Il est interdit d'en tirer un fichier de démarchage ou de publipostage.</li>
          <li>Le demandeur s'abstient de tout usage portant atteinte à la vie privée des personnes inscrites.</li>
          <li>Agissant pour un tiers, le demandeur l'informe de ces règles et ne conserve pas les informations communiquées.</li>
        </ul>
        <label className="case case-forte">
          <input type="checkbox" checked={engagement} onChange={(e) => setEngagement(e.target.checked)} />
          Le demandeur déclare avoir pris connaissance de ces règles et s'y engager.
        </label>
      </Cadre>

      {erreur && <p className="erreur" role="alert">{erreur}</p>}

      {/* ═══ LE RÉCAPITULATIF ═══════════════════════════════════════════════
          ⚠️ IL REND LE CORPS QUE `construire()` VA ENVOYER — pas une seconde
          lecture du formulaire. C'est ce qui rend la relecture opposable :
          l'agent voit exactement ce qui partira, et le certificateur lira la
          même chose avec la même fonction. */}
      {etape === RECAP && (
        <section className="cadre">
          <legend><span className="cadre-n">{RECAP}</span> Récapitulatif avant dépôt</legend>
          <p className="cadre-aide">
            Ce que vous voyez ici est exactement ce qui partira au service
            « {service(type.circuit[0])} ». Chaque bloc a son bouton « Modifier » :
            il ramène au cadre, à sa place, sans rien perdre.
          </p>
          <div className="cadre-corps">
            <FormulaireLisible
              formulaire={construire() as Record<string, unknown>}
              /**
               * ⚠️ LE BLOC RAMÈNE À SON CADRE, et la correspondance est faite
               * PAR LE TITRE, jamais par un numéro écrit à la main : les
               * numéros changent d'un type à l'autre, les titres non.
               */
              peutModifier={(cle) => Boolean(cadreDuBloc(cle))}
              onModifier={(cle) => { const c = cadreDuBloc(cle); if (c) setEtape(c.n); }}
            />
          </div>
        </section>
      )}

      <div className="fe-pied">
        <p className="fe-mention">
          {etape === RECAP
            ? <>La validation de ce formulaire <strong>ouvre un dossier de suivi</strong> et transmet
               la demande au service « {service(type.circuit[0])} ».</>
            : 'On peut passer à l’étape suivante sans l’avoir finie : le brouillon garde tout, et le rail dit ce qui reste.'}
        </p>
        <div className="actions">
          <button type="button" className="bouton discret" onClick={onFerme}>Annuler</button>
          {/* ⚠️ NAVIGUER N'EST PAS DÉPOSER : deux boutons distincts, et celui
              du dépôt n'apparaît QUE sur le récapitulatif. Un « Valider »
              visible dès le premier cadre invite à déposer un formulaire dont
              on n'a pas vu la moitié. */}
          <button type="button" className="bouton discret" disabled={etape <= 1}
                  onClick={() => setEtape((n) => Math.max(1, n - 1))}>← Étape précédente</button>
          {etape < RECAP && (
            <button type="button" className="bouton" onClick={() => setEtape((n) => n + 1)}>
              {etape === RECAP - 1 ? 'Relire avant de déposer →' : 'Étape suivante →'}
            </button>
          )}
          {etape === RECAP && totalManques > 0 && (
            <span className="fe-mention" style={{ color: 'var(--ambre)' }}>
              {totalManques} champ(s) obligatoire(s) manquent — le rail dit dans quel cadre.
            </span>
          )}
          {/* ⚠️ LE BOUTON EST FERMÉ TANT QUE LES DÉCLARATIONS MANQUENT, et ce
              n'est pas un doublon du service : celui-ci refuserait le dépôt
              avec un message Zod sur `acceptationDonataire`, que l'usager
              lirait comme une panne après avoir rempli six cadres. */}
          {etape === RECAP && (
          <button className="bouton primaire"
                  /**
                    * ⚠️ LE BOUTON RESTE FERMÉ TANT QUE LA DÉCLARATION
                    * CONSTITUTIVE MANQUE — une par type, et ce ne sont pas des
                    * formalités : sans elle, l'acte déposé n'est PAS l'acte
                    * annoncé. Une donation sans acceptation est une offre ;
                    * une vente sans déclaration de sincérité ne peut pas être
                    * reçue ; une mainlevée sans consentement du créancier est
                    * une demande adressée au créancier. Le service refuse dans
                    * les trois cas — le bouton fermé épargne à l'agent un
                    * aller-retour et un refus qu'il lirait comme une panne.
                    */
                  disabled={envoi || !engagement
                    || (type.code === 'donation' && (!acceptation || !declaration))
                    || (type.code === 'vente' && !declaration)
                    || (type.code === 'mainlevee' && !acceptation)
                    /* ⚠️ L'INSCRIPTION EXIGE LE CONSENTEMENT DU CONSTITUANT —
                       miroir de la mainlevée. La RADIATION n'en exige AUCUN :
                       elle constate une cause, elle ne se consent pas. Ajouter
                       ici une condition sur `acceptation` pour la radiation
                       fermerait le bouton sur une case que son formulaire
                       n'affiche pas — un refus sans cause visible. */
                    || (type.code === 'inscription_surete' && !acceptation)
                    /* ⚠️ ET TANT QU'UN CHAMP OBLIGATOIRE MANQUE. `noValidate`
                       a retiré le contrôle du navigateur — il refusait en
                       silence sur un champ caché ; celui-ci refuse en DISANT
                       combien il en manque et où. */
                    || totalManques > 0}>
            {envoi ? 'Enregistrement…' : 'Valider et transmettre'}
          </button>
          )}
        </div>
      </div>
      </div>
      </div>
    </ParcoursCtx.Provider>
    </form>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   LA FICHE D'UNE DEMANDE — son parcours, et le geste qui la fait avancer.
   ═══════════════════════════════════════════════════════════════════════════ */

function FicheDemandeVue(
  { reference, onFerme, onOuvrirDossier }:
  { reference: string; onFerme: () => void; onOuvrirDossier: (r: string) => void },
) {
  const [d, setD] = useState<FicheDemande | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);

  const charger = useCallback(async () => {
    setErreur(null);
    try { setD(await api.demandes.fiche(reference)); }
    catch (e) { setErreur(e instanceof ErreurApi ? e.message : String(e)); }
  }, [reference]);
  useEffect(() => { void charger(); }, [charger]);

  async function transmettre(suite: 'poursuivre' | 'aboutir' | 'rejeter') {
    const motif = window.prompt(
      suite === 'poursuivre'
        ? "Ce que votre service a fait, avant de passer la main (10 caractères minimum) :"
        : suite === 'aboutir'
          ? "Conclusion de la demande — ce qui a été délivré (10 caractères minimum) :"
          : "Motif du rejet — il sera notifié au demandeur (10 caractères minimum) :");
    if (motif === null) return;
    if (motif.trim().length < 10) { setErreur('Un motif est obligatoire : il est consigné au registre.'); return; }
    setOccupe(true); setErreur(null); setMessage(null);
    try {
      const r = await api.demandes.transmettre(reference, { motif: motif.trim(), suite });
      setMessage(r.message);
      await charger();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : String(e));
    } finally { setOccupe(false); }
  }

  if (erreur && !d) return (
    <div className="fiche-formulaire large">
      <p className="erreur" role="alert">{erreur}</p>
      <button className="bouton discret" onClick={onFerme}>Retour</button>
    </div>
  );
  if (!d) return <div className="attente-plein">Chargement de {reference}…</div>;

  const dernier = d.circuit.position >= d.circuit.services.length - 1;

  return (
    <div className="tableau-bord">
      <header className="entete-scene">
        <h2>{d.demande.libelle} <code>{d.demande.reference}</code></h2>
        <div className="actions">
          <span className={`etat ${d.demande.etat === 'aboutie' ? 'vert'
            : d.demande.etat === 'rejetee' ? 'rouge' : 'bleu'}`}>{d.demande.etat}</span>
          <button className="bouton discret" onClick={onFerme}>Retour à la file</button>
        </div>
      </header>

      {message && <p className="succes" role="status">{message}</p>}
      {erreur && <p className="erreur" role="alert">{erreur}</p>}

      {/* Le circuit, et où en est la pièce — c'est la première question. */}
      <section className="ruban-circuit">
        {d.circuit.services.map((s, i) => (
          <div key={s} className={`rc-etape ${i < d.circuit.position ? 'faite'
            : i === d.circuit.position && !d.demande.close_le ? 'courante' : 'a-venir'}`}>
            <span className="rc-n">{i + 1}</span>
            <span className="rc-nom">{service(s)}</span>
          </div>
        ))}
        {d.demande.close_le && (
          <div className={`rc-etape ${d.demande.etat === 'aboutie' ? 'faite' : 'refusee'}`}>
            <Icone nom={d.demande.etat === 'aboutie' ? 'valide' : 'refus'} taille={15} />
            <span className="rc-nom">{d.demande.etat}</span>
          </div>
        )}
      </section>

      <section>
        <div className="ligne-fiche"><span>Requérant</span><strong>{d.requerant}</strong></div>
        <div className="ligne-fiche"><span>Déposée le</span>
          <strong>{new Date(d.demande.depose_le).toLocaleDateString('fr-FR')} · par le guichet {service(d.demande.canal)}</strong></div>
        <div className="ligne-fiche"><span>Dossier de suivi</span>
          <strong><button className="lien" onClick={() => onOuvrirDossier(d.affaire.reference)}>
            <code>{d.affaire.reference}</code></button> — {d.affaire.etape}</strong></div>
        {d.parcelle && <div className="ligne-fiche"><span>Parcelle</span><strong><code>{d.parcelle}</code></strong></div>}
        {d.demande.produit && <div className="ligne-fiche"><span>Produira</span><strong>{d.demande.produit}</strong></div>}
      </section>

      <section>
        <h4>Le formulaire déposé</h4>
        {/**
          * ⚠️⚠️ C'ÉTAIT `<pre>{JSON.stringify(d.formulaire, null, 2)}</pre>`.
          * L'agent chargé de CERTIFIER l'acte lisait des clés de base de
          * données dans l'ordre du sérialiseur — mesuré sur GN-DEM-000000033
          * le 24/09/2026 : `"communeOuSousPrefecture": "matoto"`, sur le poste
          * d'un chef de service foncier. C'est le §68.
          *
          * ⚠️ MÊME FONCTION QUE LE RÉCAPITULATIF AVANT DÉPÔT, et c'est ce qui
          * rend la relecture opposable : l'agent a vu à l'écran exactement ce
          * corps-là avant de l'envoyer, et le certificateur voit le même.
          * Deux rendus divergeraient au premier champ ajouté.
          *
          * ⚠️ RIEN N'EST TU : un champ absent du catalogue des libellés est
          * rendu avec son nom brut et signalé, jamais sauté. Un récapitulatif
          * qui omet ce qu'il ne sait pas lire fait certifier un acte amputé.
          */}
        <FormulaireLisible formulaire={d.formulaire as Record<string, unknown>} />
      </section>

      <section>
        <h4>Parcours</h4>
        <ol className="fil">
          {d.parcours.map((t, k) => (
            <li key={k}>
              <span className="etat bleu">
                {t.de_service ? service(t.de_service) : 'Guichet'}
                {' → '}
                {t.vers_service ? service(t.vers_service) : 'clôture'}
              </span>
              <span className="fil-date">{new Date(t.survenu_le).toLocaleString('fr-FR')}</span>
              {t.organisation && <span className="discret"> · {t.organisation}</span>}
              {t.motif && <div className="fil-note">{t.motif}</div>}
            </li>
          ))}
        </ol>
      </section>

      {/* ⚠️ ON N'OFFRE LE GESTE QUE SI LA PIÈCE EST SUR CE BUREAU, et sinon on
          dit pourquoi. Un bouton qui rendrait 403 est pire que pas de bouton. */}
      {d.chezMoi ? (
        <div className="actions">
          {!dernier && (
            <button className="bouton primaire" disabled={occupe}
                    onClick={() => void transmettre('poursuivre')}>
              Terminer et transmettre à « {service(d.circuit.suivant)} »
            </button>
          )}
          <button className="bouton" disabled={occupe} onClick={() => void transmettre('aboutir')}>
            Conclure — demande aboutie
          </button>
          <button className="bouton discret danger" disabled={occupe}
                  onClick={() => void transmettre('rejeter')}>
            Rejeter
          </button>
        </div>
      ) : (
        <p className="note-indispo">{d.motifSiPasChezMoi}</p>
      )}
    </div>
  );
}
